import React, { useEffect, useMemo, useRef, useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  limit,
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

const BASE_PRICE = 1350;
const DISCOUNT_PERCENT = 55;
const FINAL_PRICE = Math.round(BASE_PRICE * (1 - DISCOUNT_PERCENT / 100));
const STORAGE_KEY = 'inkrodriguez_wishlist_lock_v1';

const STUDIO_WHATSAPP = '5524993087471';
const WISHLIST_CLOSED = true;
const DEATH_NOTE_PREVIEW_ACTIVE = false;
const DISPOSITIVO_UNICO_ATIVO = false;
const SITE_MUSIC_SRC = '/music/death-note-theme.mp3';


const ONE_PIECE_WIKI_API = 'https://onepiece.fandom.com/api.php';

const GEEK_PROJECTS = [
  { name: 'Garp', wikiTitle: 'Monkey D. Garp', wikiSearch: 'Monkey D. Garp', universe: 'One Piece', initials: 'GA' },
  { name: 'Bony Chan', wikiTitle: 'Bentham', wikiSearch: 'Bentham Mr. 2 Bon Kurei', universe: 'One Piece', initials: 'BC' },
  { name: 'Chopper', wikiTitle: 'Tony Tony Chopper', wikiSearch: 'Tony Tony Chopper', universe: 'One Piece', initials: 'CH' },
  { name: 'Sanji', wikiTitle: 'Sanji', wikiSearch: 'Sanji', universe: 'One Piece', initials: 'SA' },
  { name: 'Ace', wikiTitle: 'Portgas D. Ace', wikiSearch: 'Portgas D. Ace', universe: 'One Piece', initials: 'AC' },
  { name: 'Sabo', wikiTitle: 'Sabo', wikiSearch: 'Sabo One Piece', universe: 'One Piece', initials: 'SB' },
  { name: 'Zoro', wikiTitle: 'Roronoa Zoro', wikiSearch: 'Roronoa Zoro', universe: 'One Piece', initials: 'ZO' },
  { name: 'Corazon', wikiTitle: 'Donquixote Rosinante', wikiSearch: 'Donquixote Rosinante Corazon', universe: 'One Piece', initials: 'CO' },
  { name: 'Doflamingo', wikiTitle: 'Donquixote Doflamingo', wikiSearch: 'Donquixote Doflamingo', universe: 'One Piece', initials: 'DO' },
  { name: 'Law', wikiTitle: 'Trafalgar D. Water Law', wikiSearch: 'Trafalgar D. Water Law', universe: 'One Piece', initials: 'LA' },
  { name: 'Kyros', wikiTitle: 'Kyros', wikiSearch: 'Kyros One Piece', universe: 'One Piece', initials: 'KY' },
  { name: 'Dragon', wikiTitle: 'Monkey D. Dragon', wikiSearch: 'Monkey D. Dragon', universe: 'One Piece', initials: 'DR' },
  { name: 'Crocodile', wikiTitle: 'Crocodile', wikiSearch: 'Crocodile One Piece', universe: 'One Piece', initials: 'CR' },
  { name: 'Kid', wikiTitle: 'Eustass Kid', wikiSearch: 'Eustass Kid', universe: 'One Piece', initials: 'KI' },
  { name: 'Bartolomeo', wikiTitle: 'Bartolomeo', wikiSearch: 'Bartolomeo One Piece', universe: 'One Piece', initials: 'BA' },
  { name: 'Kaidou', wikiTitle: 'Kaidou', wikiSearch: 'Kaidou Kaido', universe: 'One Piece', initials: 'KA' },
  { name: 'Shanks', wikiTitle: 'Shanks', wikiSearch: 'Shanks One Piece', universe: 'One Piece', initials: 'SH' },
  { name: 'Marco', wikiTitle: 'Marco', wikiSearch: 'Marco The Phoenix', universe: 'One Piece', initials: 'MF' },
];

const normalizeSearchText = (value = '') =>
  String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const getMediaWikiPages = (payload) => {
  const pages = payload?.query?.pages;
  if (!pages || typeof pages !== 'object') return [];
  return Object.values(pages).filter((page) => page && !page.missing);
};

const getMediaWikiPageImage = (page) =>
  page?.thumbnail?.source ||
  page?.original?.source ||
  page?.pageimage ||
  '';

const buildOnePieceWikiUrl = (params) => {
  const searchParams = new URLSearchParams({
    format: 'json',
    origin: '*',
    ...params,
  });

  return `${ONE_PIECE_WIKI_API}?${searchParams.toString()}`;
};

const findBestWikiPage = (pages, project) => {
  const title = normalizeSearchText(project.wikiTitle);
  const search = normalizeSearchText(project.wikiSearch);
  const displayName = normalizeSearchText(project.name);

  return (
    pages.find((page) => normalizeSearchText(page.title) === title) ||
    pages.find((page) => normalizeSearchText(page.title).includes(title)) ||
    pages.find((page) => normalizeSearchText(page.title).includes(displayName)) ||
    pages.find((page) => normalizeSearchText(page.title).includes(search)) ||
    pages[0] ||
    null
  );
};

const fetchWikiPageImageByTitle = async (project, signal) => {
  const apiUrl = buildOnePieceWikiUrl({
    action: 'query',
    titles: project.wikiTitle,
    redirects: '1',
    prop: 'pageimages|info',
    piprop: 'thumbnail|original',
    pithumbsize: '900',
    inprop: 'url',
  });

  const response = await fetch(apiUrl, { signal });
  if (!response.ok) return null;

  const payload = await response.json();
  const page = findBestWikiPage(getMediaWikiPages(payload), project);
  const image = getMediaWikiPageImage(page);

  if (!image) return null;

  return {
    image,
    apiName: page?.title || project.wikiTitle,
    apiUrl: page?.fullurl || '',
  };
};

const fetchWikiPageImageBySearch = async (project, signal) => {
  const apiUrl = buildOnePieceWikiUrl({
    action: 'query',
    generator: 'search',
    gsrsearch: project.wikiSearch || project.wikiTitle || project.name,
    gsrlimit: '5',
    prop: 'pageimages|info',
    piprop: 'thumbnail|original',
    pithumbsize: '900',
    inprop: 'url',
  });

  const response = await fetch(apiUrl, { signal });
  if (!response.ok) return null;

  const payload = await response.json();
  const page = findBestWikiPage(getMediaWikiPages(payload), project);
  const image = getMediaWikiPageImage(page);

  if (!image) return null;

  return {
    image,
    apiName: page?.title || project.wikiTitle,
    apiUrl: page?.fullurl || '',
  };
};

const fetchProjectImageFromOnePieceApi = async (project, signal) => {
  try {
    const result =
      (await fetchWikiPageImageByTitle(project, signal)) ||
      (await fetchWikiPageImageBySearch(project, signal));

    if (result?.image) {
      return {
        ...project,
        image: result.image,
        apiName: result.apiName,
        apiUrl: result.apiUrl,
        apiStatus: 'loaded',
        apiSource: 'One Piece Wiki API',
      };
    }
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.warn(`Erro ao buscar imagem de ${project.name} na One Piece Wiki API:`, error);
  }

  return {
    ...project,
    image: '',
    apiName: project.wikiTitle,
    apiUrl: '',
    apiStatus: 'empty',
    apiSource: '',
  };
};

const injectedStyles = `
  @font-face {
    font-family: "Death Note";
    src:
      url("/fonts/Death%20Note.woff2") format("woff2"),
      url("/fonts/Death Note.woff2") format("woff2"),
      url("/fonts/Death%20Note/Death%20Note.woff2") format("woff2"),
      url("/fonts/Death Note/Death Note.woff2") format("woff2"),
      url("/fonts/Death%20Note.woff") format("woff"),
      url("/fonts/Death Note.woff") format("woff"),
      url("/fonts/Death%20Note/Death%20Note.woff") format("woff"),
      url("/fonts/Death Note/Death Note.woff") format("woff"),
      url("/fonts/Death%20Note.ttf") format("truetype"),
      url("/fonts/Death Note.ttf") format("truetype"),
      url("/fonts/Death%20Note/Death%20Note.ttf") format("truetype"),
      url("/fonts/Death Note/Death Note.ttf") format("truetype"),
      url("/fonts/Death%20Note.otf") format("opentype"),
      url("/fonts/Death Note.otf") format("opentype"),
      url("/fonts/Death%20Note/Death%20Note.otf") format("opentype"),
      url("/fonts/Death Note/Death Note.otf") format("opentype"),
      url("/Death%20Note.woff2") format("woff2"),
      url("/Death Note.woff2") format("woff2"),
      url("/Death%20Note/Death%20Note.woff2") format("woff2"),
      url("/Death Note/Death Note.woff2") format("woff2"),
      url("/Death%20Note.woff") format("woff"),
      url("/Death Note.woff") format("woff"),
      url("/Death%20Note/Death%20Note.woff") format("woff"),
      url("/Death Note/Death Note.woff") format("woff"),
      url("/Death%20Note.ttf") format("truetype"),
      url("/Death Note.ttf") format("truetype"),
      url("/Death%20Note/Death%20Note.ttf") format("truetype"),
      url("/Death Note/Death Note.ttf") format("truetype"),
      url("/Death%20Note.otf") format("opentype"),
      url("/Death Note.otf") format("opentype"),
      url("/Death%20Note/Death%20Note.otf") format("opentype"),
      url("/Death Note/Death Note.otf") format("opentype");
    font-weight: 300 800;
    font-style: normal;
    font-display: swap;
  }

  * {
    box-sizing: border-box;
  }

  html, body {
    width: 100%;
    max-width: 100%;
    min-height: 100%;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
    background: #0d0d0d;
  }

  #root {
    width: 100%;
    max-width: 100%;
    min-height: 100vh;
    overflow-x: hidden;
    background:
      radial-gradient(circle at top, rgba(255,255,255,0.06), transparent 30%),
      linear-gradient(180deg, #0d0d0d 0%, #131313 100%);
  }

  .ink-login-screen {
    min-height: 100vh;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    background:
      radial-gradient(circle at top, rgba(255,255,255,0.06), transparent 30%),
      linear-gradient(180deg, #0d0d0d 0%, #131313 100%);
    color: #fff;
    padding: 12px 16px 42px;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .ink-login-shell {
    width: 100%;
    max-width: 1120px;
    margin: 0 auto;
    overflow-x: hidden;
  }

  .ink-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    margin-bottom: 18px;
    width: 100%;
  }

  .ink-enter-top {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
    padding-top: -8px;
  }

  .ink-enter-top .ink-btn-outline {
    flex: 0 0 auto;
  }

  .ink-brand-wrap {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .ink-brand {
    margin: 0;
    line-height: 0.92;
    display: flex;
    flex-direction: column;
    align-items: center;
    letter-spacing: 0.08em;
    user-select: none;
    max-width: 100%;
  }

.ink-brand-main {
  font-size: clamp(4.2rem, 8vw, 6.5rem);
  font-weight: 900;
  position: relative;
  left: -2px;
}

  .ink-brand-sub {
    font-size: clamp(1.65rem, 4.8vw, 3.1rem);
    font-weight: 700;
    opacity: 0.95;
  }

  .ink-btn {
    border: 0;
    outline: 0;
    cursor: pointer;
    border-radius: 18px;
    padding: 13px 18px;
    font-weight: 800;
    transition: transform .18s ease, opacity .18s ease, box-shadow .18s ease;
    text-decoration: none;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    max-width: 100%;
  }

  .ink-btn:hover {
    transform: translateY(-1px);
  }

  .ink-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }

  .ink-btn-outline {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.18);
    color: #fff;
    width: 96px;
    min-width: 96px;
    padding: 9px 12px;
    border-radius: 13px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.22);
    font-size: 0.92rem;
  }

  .ink-btn-primary {
    background: linear-gradient(135deg, #ff5b5b 0%, #d93a3a 100%);
    color: #fff;
    box-shadow: 0 14px 40px rgba(255,91,91,0.25);
  }

  .ink-btn-blue {
    background: linear-gradient(135deg, #50b7ff 0%, #2878ff 100%);
    color: #fff;
    box-shadow: 0 14px 40px rgba(40,120,255,0.25);
  }

  .ink-btn-green {
    background: linear-gradient(135deg, #25d366 0%, #179f4c 100%);
    color: #fff;
    box-shadow: 0 14px 40px rgba(37,211,102,0.25);
  }

  .ink-btn-dark {
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.12);
    color: #fff;
  }

  .ink-btn-warning {
    background: linear-gradient(135deg, #ff5b5b 0%, #d93a3a 100%);
    color: #fff;
    box-shadow: 0 14px 40px rgba(255,91,91,0.25);
  }

  .ink-main-card {
    width: 100%;
    max-width: 920px;
    margin: 0 auto;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.10);
    backdrop-filter: blur(10px);
    border-radius: 28px;
    padding: 24px;
    box-shadow: 0 18px 60px rgba(0,0,0,0.34);
    overflow: visible;
  }

  .ink-main-card-death {
    position: relative;
    overflow: visible;
    border-radius: 30px;
    border: 1px solid rgba(255, 0, 0, 0.18);
    background:
      radial-gradient(circle at top left, rgba(255, 0, 0, 0.16), transparent 34%),
      radial-gradient(circle at bottom right, rgba(255, 255, 255, 0.05), transparent 38%),
      linear-gradient(135deg, #070707 0%, #121212 46%, #050505 100%);
    box-shadow:
      0 22px 70px rgba(0,0,0,0.42),
      inset 0 0 60px rgba(255,0,0,0.04);
  }

  .ink-main-card-death::before {
    content: '';
    position: absolute;
    inset: -80px;
    background:
      radial-gradient(circle, rgba(255,255,255,0.06) 0 1px, transparent 1px 100%);
    background-size: 38px 38px;
    opacity: 0.22;
    transform: rotate(-8deg);
    pointer-events: none;
  }

  .ink-main-card-inner {
    position: relative;
    z-index: 1;
  }

  .ink-main-card-death .ink-death-book {
    width: 100%;
    max-width: 640px;
    margin: 0 auto;
  }

  .ink-main-card-death .ink-public-switch {
    margin-bottom: 20px;
  }

  .ink-main-head {
    text-align: center;
    margin-bottom: 16px;
  }

  .ink-main-head h2 {
    margin: 0 0 8px;
    font-size: clamp(1.35rem, 3vw, 2rem);
    color: #fff;
  }

  .ink-main-head p {
    margin: 0;
    color: rgba(255,255,255,0.78);
    font-size: 0.98rem;
  }

  .ink-promo-card {
    margin: 0 auto 18px;
    width: 100%;
    background: linear-gradient(135deg, rgba(255,91,91,0.16), rgba(255,255,255,0.05));
    border: 1px solid rgba(255,91,91,0.30);
    border-radius: 20px;
    padding: 16px 18px;
    text-align: center;
    box-shadow: 0 10px 35px rgba(255,91,91,0.10);
  }

  .ink-promo-card strong {
    display: block;
    font-size: 1rem;
    letter-spacing: 0.08em;
    color: #ff7b7b;
  }

  .ink-fields-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 22px;
    width: 100%;
  }

  .ink-field {
    position: relative;
    min-width: 0;
  }

  .ink-field label {
    display: block;
    font-size: 0.88rem;
    font-weight: 700;
    margin-bottom: 8px;
    color: rgba(255,255,255,0.88);
  }

  .ink-field input {
    width: 100%;
    max-width: 100%;
    border: 1px solid rgba(255,255,255,0.14);
    background: rgba(255,255,255,0.08);
    color: #fff;
    border-radius: 16px;
    padding: 14px 14px;
    font-size: 0.96rem;
    outline: none;
  }

  .ink-field input::placeholder {
    color: rgba(255,255,255,0.42);
  }

  .ink-field input:focus {
    border-color: rgba(255,91,91,0.72);
    box-shadow: 0 0 0 3px rgba(255,91,91,0.10);
  }

  .ink-field input[disabled] {
    opacity: 0.72;
    cursor: not-allowed;
  }

  .ink-loading-text,
  .ink-error-text,
  .ink-status-text {
    margin: 8px 0 0;
    font-size: 0.83rem;
    color: rgba(255,255,255,0.78);
  }

  .ink-suggestions {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    z-index: 30;
    background: #111;
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 18px;
    overflow: visible;
    box-shadow: 0 20px 50px rgba(0,0,0,0.48);
    max-height: none;
    max-width: 100%;
  }

  .ink-suggestion-btn {
    width: 100%;
    border: 0;
    background: transparent;
    color: #fff;
    text-align: left;
    padding: 14px 14px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }

  .ink-suggestion-btn:last-child {
    border-bottom: 0;
  }

  .ink-suggestion-btn:hover {
    background: rgba(255,255,255,0.06);
  }

  .ink-suggestion-name {
    font-weight: 800;
    font-size: 0.96rem;
  }

  .ink-suggestion-subtitle {
    font-size: 0.82rem;
    color: rgba(255,255,255,0.72);
  }

  .ink-selected-card {
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 18px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    min-height: 72px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .ink-selected-thumb {
    width: 54px;
    height: 54px;
    border-radius: 14px;
    object-fit: cover;
    background: rgba(255,255,255,0.08);
    flex-shrink: 0;
  }

  .ink-selected-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .ink-selected-info strong {
    font-size: 0.95rem;
    line-height: 1.2;
    word-break: break-word;
  }

  .ink-selected-info span {
    margin-top: 4px;
    font-size: 0.8rem;
    color: rgba(255,255,255,0.7);
    line-height: 1.2;
    word-break: break-word;
  }

  .ink-actions {
    display: flex;
    justify-content: center;
    margin-top: 8px;
    width: 100%;
  }

  .ink-sort-btn {
    min-width: 220px;
    max-width: 100%;
  }

  .ink-feedback {
    margin-top: 14px;
    text-align: center;
    font-size: 0.92rem;
    color: #ff7b7b;
    font-weight: 700;
  }

  .ink-result-wrap {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    width: 100%;
  }

  .ink-capture-card {
    width: 100%;
    background:
      radial-gradient(circle at top, rgba(255,255,255,0.05), transparent 32%),
      linear-gradient(180deg, #111111 0%, #171717 100%);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 26px;
    padding: 22px;
    box-shadow: 0 16px 50px rgba(0,0,0,0.28);
  }

  .ink-capture-title {
    text-align: center;
    margin-bottom: 14px;
  }

  .ink-capture-title strong {
    color: #ff7b7b;
    font-size: 0.95rem;
    letter-spacing: 0.08em;
  }

  .ink-opportunity-box {
    text-align: center;
    border-radius: 20px;
    padding: 16px;
    background:
      radial-gradient(circle at top, rgba(255,91,91,0.15), transparent 60%),
      rgba(255,255,255,0.04);
    border: 1px solid rgba(255,91,91,0.28);
  }

  .ink-opportunity-title {
    margin: 0;
    color: #ff7b7b;
    font-size: 1.12rem;
    font-weight: 900;
    letter-spacing: 0.06em;
    text-shadow:
      0 0 8px rgba(255, 91, 91, 0.35),
      0 0 18px rgba(255, 91, 91, 0.18);
  }

  .ink-opportunity-subtitle {
    margin: 8px 0 0;
    color: rgba(255,255,255,0.82);
    font-size: 0.96rem;
    font-weight: 700;
  }

  .ink-result-character {
    display: flex;
    align-items: center;
    gap: 18px;
    padding: 18px;
    border-radius: 24px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.09);
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .ink-result-image {
    width: 176px;
    height: 176px;
    object-fit: cover;
    border-radius: 26px;
    border: 1px solid rgba(255,255,255,0.08);
    flex-shrink: 0;
    background: rgba(255,255,255,0.06);
  }

  .ink-result-info {
    min-width: 0;
  }

  .ink-result-info h3 {
    margin: 0;
    font-size: 1.9rem;
    line-height: 1.02;
    word-break: break-word;
  }

  .ink-result-origin {
    margin-top: 6px;
    color: rgba(255,255,255,0.72);
    font-size: 1rem;
    word-break: break-word;
  }

  .ink-reserve-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 16px;
  }

  .ink-reserve-price-box {
    text-align: center;
    border-radius: 22px;
    padding: 18px 16px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
  }

  .ink-pix-card-highlight {
    border: 1px solid rgba(255,91,91,0.22);
    background:
      radial-gradient(circle at top, rgba(255,91,91,0.12), transparent 55%),
      rgba(255,255,255,0.05);
  }

  .ink-reserve-price-label {
    display: block;
    font-size: 0.84rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(255,255,255,0.65);
    margin-bottom: 8px;
  }

  .ink-reserve-price-from {
    font-size: 1.02rem;
    line-height: 1.2;
    color: #ff5b5b;
    font-weight: 800;
    text-decoration: line-through;
    text-decoration-thickness: 2px;
    text-decoration-color: #ff5b5b;
  }

  .ink-reserve-price-to {
    margin-top: 8px;
    font-size: clamp(1.5rem, 4vw, 2.2rem);
    line-height: 1;
    font-weight: 900;
    color: #4dff88;
    text-shadow:
      0 0 8px rgba(77,255,136,0.30),
      0 0 18px rgba(77,255,136,0.20);
  }

  .ink-reserve-discount-value {
    font-size: clamp(1.8rem, 4vw, 2.6rem);
    font-weight: 900;
    line-height: 0.95;
    color: #ff5b5b;
    text-shadow:
      0 0 8px rgba(255,91,91,0.28),
      0 0 18px rgba(255,91,91,0.18);
  }

  .ink-result-size {
    text-align: center;
    color: rgba(255,255,255,0.82);
    font-size: 1rem;
    margin-top: 14px;
  }

  .ink-result-cta {
    display: flex;
    justify-content: center;
    width: 100%;
    flex-wrap: wrap;
    gap: 12px;
  }

  .ink-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.76);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 18px;
    z-index: 1000;
    overflow-y: visible;
  }

  .ink-modal {
    width: 100%;
    max-width: 560px;
    max-height: none;
    overflow: visible;
    background: #111;
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 24px;
    padding: 18px 18px 16px;
    position: relative;
    box-shadow: 0 24px 70px rgba(0,0,0,0.5);
  }

  .ink-modal h2 {
    margin: 0;
  }

  .ink-close {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    border: 0;
    cursor: pointer;
    background: rgba(255,255,255,0.08);
    color: #fff;
    font-size: 1rem;
  }

  .ink-auth-subtitle {
    color: rgba(255,255,255,0.75);
    margin-top: 0;
  }

  .ink-form {
    margin-top: 14px;
    display: grid;
    gap: 12px;
  }

  .ink-form-group label {
    display: block;
    margin-bottom: 6px;
    font-size: 0.86rem;
    font-weight: 700;
  }

  .ink-form-group input {
    width: 100%;
    border: 1px solid rgba(255,255,255,0.14);
    background: rgba(255,255,255,0.08);
    color: #fff;
    border-radius: 14px;
    padding: 12px 14px;
    font-size: 0.95rem;
    outline: none;
  }

  .ink-form-group input:focus {
    border-color: rgba(255,91,91,0.72);
    box-shadow: 0 0 0 3px rgba(255,91,91,0.10);
  }

  .ink-full {
    width: 100%;
  }

  .ink-pix-box {
    display: grid;
    gap: 14px;
    margin-top: 12px;
  }

  .ink-pix-card {
    border-radius: 20px;
    padding: 16px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
  }

  .ink-pix-title {
    font-size: 1.05rem;
    font-weight: 900;
    margin: 0 0 8px;
  }

  .ink-pix-sub {
    margin: 0;
    color: rgba(255,255,255,0.72);
    font-size: 0.92rem;
  }

  .ink-pix-qr-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 6px;
  }

  .ink-pix-qr {
    width: 220px;
    height: 220px;
    object-fit: contain;
    border-radius: 18px;
    background: #fff;
    padding: 8px;
  }

  .ink-pix-code {
    width: 100%;
    min-height: 110px;
    resize: vertical;
    border: 1px solid rgba(255,255,255,0.14);
    background: rgba(255,255,255,0.08);
    color: #fff;
    border-radius: 14px;
    padding: 12px 14px;
    font-size: 0.9rem;
    outline: none;
  }

  .ink-pix-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .ink-status-paid {
    color: #4dff88;
    font-weight: 900;
  }

  .ink-status-pending {
    color: #ff7b7b;
    font-weight: 900;
  }


  .ink-death-shell {
    position: relative;
    overflow: visible;
    border-radius: 30px;
    border: 1px solid rgba(255, 0, 0, 0.18);
    background:
      radial-gradient(circle at top left, rgba(255, 0, 0, 0.16), transparent 34%),
      radial-gradient(circle at bottom right, rgba(255, 255, 255, 0.05), transparent 38%),
      linear-gradient(135deg, #070707 0%, #121212 46%, #050505 100%);
    padding: 24px;
    box-shadow:
      0 22px 70px rgba(0,0,0,0.42),
      inset 0 0 60px rgba(255,0,0,0.04);
  }

  .ink-death-shell::before {
    content: '';
    position: absolute;
    inset: -80px;
    background:
      radial-gradient(circle, rgba(255,255,255,0.06) 0 1px, transparent 1px 100%);
    background-size: 38px 38px;
    opacity: 0.22;
    transform: rotate(-8deg);
    pointer-events: none;
  }

  .ink-death-hero-grid {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 0.9fr) minmax(320px, 1.1fr);
    gap: 22px;
    align-items: flex-start;
  }

  .ink-death-copy {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-width: 0;
    padding-top: 4px;
  }

  .ink-death-badge {
    display: none;
    width: fit-content;
    /* display: inline-flex; */
    align-items: center;
    justify-content: center;
    padding: 8px 12px;
    border-radius: 999px;
    background: rgba(255, 0, 0, 0.13);
    border: 1px solid rgba(255, 0, 0, 0.28);
    color: #ff7474;
    font-size: 0.78rem;
    font-weight: 950;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    margin-bottom: 14px;
    box-shadow: 0 0 22px rgba(255, 0, 0, 0.13);
  }

  .ink-death-title {
    margin: 0;
    font-family: "Death Note", "Old English Text MT", "Cloister Black", Georgia, serif;
    font-size: clamp(3.2rem, 7vw, 6.8rem);
    line-height: 0.78;
    letter-spacing: 0.015em;
    font-weight: 700;
    color: #fff;
    text-transform: none;
    white-space: nowrap;
    text-shadow:
      0 0 6px rgba(255, 255, 255, 0.20),
      0 0 18px rgba(255, 0, 0, 0.34),
      0 10px 34px rgba(0,0,0,0.62);
  }

  .ink-death-title span {
    font-family: inherit;
    color: #ff2f2f;
    display: inline;
    letter-spacing: 0.035em;
    text-shadow:
      0 0 8px rgba(255, 47, 47, 0.36),
      0 0 22px rgba(255, 0, 0, 0.24),
      0 10px 34px rgba(0,0,0,0.62);
  }

  .ink-death-text {
    margin: 16px 0 0;
    color: rgba(255,255,255,0.76);
    line-height: 1.7;
    font-size: 1rem;
  }

  .ink-death-book {
    position: relative;
    z-index: 30;
    min-height: 380px;
    border-radius: 0;
    padding: 0;
    margin-top: -20px;
    background: transparent;
    border: 0;
    box-shadow: none;
    overflow: visible;
  }

  .ink-death-book.is-writing {
    animation: deathBookShake .18s linear infinite;
  }

  .ink-death-book::before {
    content: none;
    display: none;
  }

  .ink-death-page {
    position: relative;
    z-index: 1;
    min-height: 344px;
    border-radius: 18px;
    padding: 92px 18px 20px;
    background:
      linear-gradient(90deg, rgba(79, 41, 24, 0.16), transparent 9%, transparent 91%, rgba(79, 41, 24, 0.13)),
      repeating-linear-gradient(
        180deg,
        rgba(36, 22, 12, 0.10) 0px,
        rgba(36, 22, 12, 0.10) 1px,
        transparent 1px,
        transparent 34px
      ),
      linear-gradient(180deg, #d8c49a 0%, #bca271 100%);
    color: #1a0f0a;
    border: 1px solid rgba(255,255,255,0.10);
    box-shadow:
      inset 0 0 30px rgba(50, 24, 8, 0.30),
      inset 12px 0 28px rgba(60, 28, 8, 0.18);
    overflow: visible;
  }

  .ink-death-page::before {
    content: '';
    position: absolute;
    left: 48px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: rgba(143, 22, 22, 0.28);
  }

  .ink-death-page-title {
    position: absolute;
    top: 20px;
    left: 18px;
    right: 18px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    font-family: "Death Note", "Old English Text MT", "Cloister Black", Georgia, serif;
    text-align: center;
  }

  .ink-death-page-title strong {
    color: #160b06;
    font-size: 1.95rem;
    letter-spacing: 0.06em;
    font-weight: 700;
    text-transform: none;
    text-shadow: 0 1px 0 rgba(255,255,255,0.16);
  }

  .ink-death-page-title span {
    color: rgba(26, 15, 10, 0.72);
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .ink-death-lines {
    display: grid;
    gap: 4px;
    width: 100%;
  }

  .ink-death-name-line {
    position: relative;
    min-height: 64px;
    width: 100%;
    padding: 8px 0 8px 38px;
    border-radius: 0;
    background: transparent;
    border: 0;
    border-bottom: 1px solid rgba(70, 35, 13, 0.28);
    font-family: Georgia, "Times New Roman", serif;
    color: #241109;
    overflow: visible;
  }

  .ink-death-name-number {
    position: absolute;
    left: 4px;
    top: 12px;
    color: rgba(36, 17, 9, 0.48);
    font-weight: 900;
  }

  .ink-death-name-text {
    position: relative;
    display: inline-block;
    max-width: 100%;
    font-size: 1.14rem;
    line-height: 1.25;
    font-weight: 900;
    word-break: break-word;
  }

  .ink-death-name-origin {
    display: block;
    margin-top: 3px;
    color: rgba(36, 17, 9, 0.58);
    font-family: Inter, system-ui, sans-serif;
    font-size: 0.77rem;
    font-weight: 800;
  }

  .ink-death-line-body {
    position: relative;
    width: 100%;
    min-width: 0;
  }

  .ink-death-name-input {
    position: relative;
    z-index: 2;
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #241109;
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(1.26rem, 3vw, 1.58rem);
    line-height: 1.18;
    font-weight: 900;
    padding: 0;
    margin: 0;
    caret-color: #7d0000;
    text-transform: uppercase;
  }

  .ink-death-name-input::placeholder {
    color: rgba(36, 17, 9, 0.34);
    text-transform: none;
    font-style: italic;
  }

  .ink-death-name-input:focus {
    text-shadow: 0 0 10px rgba(120, 0, 0, 0.12);
  }

  .ink-death-name-input:disabled {
    opacity: 1;
    cursor: not-allowed;
  }

  .ink-death-name-input.is-winner {
    color: #2b0805;
  }

  .ink-death-helper {
    position: relative;
    z-index: 2;
    display: block;
    margin-top: 4px;
    color: rgba(36, 17, 9, 0.55);
    font-family: Inter, system-ui, sans-serif;
    font-size: 0.74rem;
    font-weight: 800;
  }

  .ink-death-suggestions {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 8px);
    z-index: 999;
    background: #130d09;
    border: 1px solid rgba(36, 17, 9, 0.25);
    border-radius: 14px;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    box-shadow: 0 18px 45px rgba(0,0,0,0.42);
    max-height: min(260px, 45vh);
  }

  .ink-death-suggestion-btn {
    width: 100%;
    border: 0;
    background: transparent;
    color: #f2dfb6;
    text-align: left;
    padding: 11px 12px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 3px;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    touch-action: pan-y;
  }

  .ink-death-suggestion-btn:last-child {
    border-bottom: 0;
  }

  .ink-death-suggestion-btn:hover {
    background: rgba(255, 0, 0, 0.13);
  }

  .ink-death-suggestion-btn strong {
    font-size: 0.92rem;
    line-height: 1.1;
  }

  .ink-death-suggestion-btn span {
    color: rgba(242, 223, 182, 0.66);
    font-size: 0.76rem;
    font-weight: 800;
  }

  .ink-death-name-empty {
    color: rgba(36, 17, 9, 0.38);
    font-style: italic;
    font-weight: 800;
  }

  .ink-death-name-line.is-winner {
    background: transparent;
    border-bottom-color: rgba(70, 35, 13, 0.38);
  }

  .ink-death-name-line.is-winner .ink-death-name-input {
    text-shadow:
      0 0 1px rgba(43, 8, 5, 0.28),
      0 0 12px rgba(120, 0, 0, 0.10);
  }

  .ink-blood-svg-defs {
    position: absolute;
    width: 0;
    height: 0;
    overflow: hidden;
    pointer-events: none;
  }

  .ink-blood-strike {
    position: absolute;
    left: -10px;
    right: -6px;
    top: -8px;
    height: 56px;
    z-index: 8;
    pointer-events: none;
    overflow: visible;
    transform: rotate(-2.4deg);
    transform-origin: left center;
    filter: url(#inkBloodRoughFilter) drop-shadow(0 2px 3px rgba(45, 0, 0, 0.42));
  }

  .ink-blood-strike::before {
    content: '';
    position: absolute;
    left: 2%;
    right: 4%;
    top: 6px;
    height: 36px;
    opacity: 0;
    background:
      radial-gradient(circle at 9% 46%, rgba(105,0,0,0.30) 0 3px, transparent 4px),
      radial-gradient(circle at 18% 35%, rgba(170,0,0,0.24) 0 2px, transparent 3px),
      radial-gradient(circle at 37% 58%, rgba(95,0,0,0.28) 0 2px, transparent 4px),
      radial-gradient(circle at 59% 42%, rgba(140,0,0,0.22) 0 3px, transparent 4px),
      radial-gradient(circle at 79% 56%, rgba(85,0,0,0.26) 0 2px, transparent 4px),
      radial-gradient(circle at 91% 40%, rgba(180,0,0,0.18) 0 2px, transparent 3px);
    animation: inkBloodMist .55s ease-out 0s forwards;
  }

  .ink-blood-main,
  .ink-blood-secondary,
  .ink-blood-drip,
  .ink-blood-splatter {
    position: absolute;
    display: block;
  }

  .ink-blood-main {
    left: 0;
    top: 14px;
    width: calc(100% + 22px);
    height: 12px;
    border-radius: 56% 42% 62% 38%;
    background:
      linear-gradient(180deg, rgba(255,255,255,0.18), transparent 34%),
      linear-gradient(90deg,
        transparent 0%,
        #350000 3%,
        #8f0000 12%,
        #d00000 28%,
        #6a0000 46%,
        #bd0000 64%,
        #4b0000 82%,
        transparent 100%
      );
    clip-path: polygon(
      0% 56%, 5% 25%, 10% 48%, 16% 18%, 23% 44%, 29% 26%, 35% 58%, 43% 20%,
      50% 46%, 57% 24%, 64% 55%, 72% 30%, 81% 61%, 90% 24%, 100% 50%,
      100% 73%, 91% 92%, 82% 68%, 74% 86%, 66% 66%, 58% 91%, 50% 70%, 42% 90%,
      34% 67%, 27% 85%, 19% 70%, 10% 90%, 4% 68%, 0% 76%
    );
    transform: scaleX(0) skewX(-8deg);
    transform-origin: left center;
    opacity: 0;
    mix-blend-mode: multiply;
    animation: inkBloodSlash .62s cubic-bezier(.12,.82,.18,1) 0s forwards;
  }

  .ink-blood-secondary {
    left: 4%;
    top: 24px;
    width: 92%;
    height: 4px;
    border-radius: 999px;
    background: linear-gradient(90deg, transparent, #410000 8%, #950000 34%, #240000 72%, transparent);
    clip-path: polygon(0 55%, 8% 30%, 18% 80%, 31% 36%, 42% 63%, 51% 28%, 66% 74%, 79% 40%, 91% 72%, 100% 48%, 100% 100%, 0 100%);
    transform: scaleX(0) rotate(0.8deg);
    transform-origin: left center;
    opacity: 0;
    animation: inkBloodScratch .48s ease-out 0s forwards;
  }

  .ink-blood-splatter {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background:
      radial-gradient(circle at 32% 28%, rgba(255,255,255,0.20), transparent 26%),
      radial-gradient(circle, #b40000 0 45%, #480000 72%, transparent 75%);
    opacity: 0;
    transform: scale(0) translateY(0);
    mix-blend-mode: multiply;
    animation: inkBloodPop .72s cubic-bezier(.16,.96,.32,1.25) forwards;
  }

  .ink-blood-splatter.splatter-a { left: 9%; top: 7px; animation-delay: 0s; }
  .ink-blood-splatter.splatter-b { left: 25%; top: 36px; width: 5px; height: 5px; animation-delay: 0s; }
  .ink-blood-splatter.splatter-c { left: 52%; top: 9px; width: 8px; height: 8px; animation-delay: 0s; }
  .ink-blood-splatter.splatter-d { left: 76%; top: 34px; width: 5px; height: 5px; animation-delay: 0s; }
  .ink-blood-splatter.splatter-e { left: 90%; top: 14px; width: 6px; height: 6px; animation-delay: 0s; }

  .ink-blood-drip {
    top: 22px;
    width: 5px;
    height: 0;
    border-radius: 0 0 999px 999px;
    background:
      linear-gradient(180deg, #8f0000 0%, #4c0000 68%, #1c0000 100%);
    opacity: 0;
    transform-origin: top center;
    mix-blend-mode: multiply;
    animation: inkBloodDrip .92s cubic-bezier(.2,.7,.18,1) forwards;
  }

  .ink-blood-drip::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -5px;
    width: 9px;
    height: 9px;
    border-radius: 999px 999px 999px 60%;
    background: radial-gradient(circle at 35% 30%, rgba(255,255,255,0.18), transparent 24%), #4b0000;
    transform: translateX(-50%) rotate(45deg) scale(0.85);
  }

  .ink-blood-drip.drip-a { left: 17%; animation-delay: 0s; }
  .ink-blood-drip.drip-b { left: 47%; width: 4px; animation-delay: 0s; }
  .ink-blood-drip.drip-c { left: 82%; height: 0; animation-delay: 0s; }

  .ink-death-name-line.is-winner .ink-blood-strike {
    opacity: 1;
  }

  .ink-death-name-line.is-winner .ink-blood-main,
  .ink-death-name-line.is-winner .ink-blood-secondary,
  .ink-death-name-line.is-winner .ink-blood-strike::before,
  .ink-death-name-line.is-winner .ink-blood-splatter,
  .ink-death-name-line.is-winner .ink-blood-drip {
    animation-delay: 0s !important;
  }

  .ink-death-writing-overlay {
    position: absolute;
    inset: 18px;
    z-index: 3;
    border-radius: 18px;
    background: rgba(0,0,0,0.74);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #fff;
    text-align: center;
    padding: 20px;
    backdrop-filter: blur(3px);
  }

  .ink-death-pen {
    width: 118px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(90deg, #e7e7e7, #191919 42%, #b00000);
    transform: rotate(-24deg);
    box-shadow: 0 0 24px rgba(255, 0, 0, 0.30);
    animation: deathPenMove .85s ease-in-out infinite alternate;
    margin-bottom: 22px;
  }

  .ink-death-writing-overlay strong {
    color: #ff5b5b;
    font-size: 1.15rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .ink-death-writing-overlay span {
    margin-top: 8px;
    color: rgba(255,255,255,0.72);
    font-size: 0.94rem;
  }

  .ink-death-final-phrase {
    margin: 14px 0 0;
    text-align: center;
    color: rgba(36, 17, 9, 0.72);
    font-family: Georgia, "Times New Roman", serif;
    font-size: 1rem;
    font-weight: 900;
  }

  .ink-death-whatsapp-note {
    margin-top: 12px;
    color: rgba(255,255,255,0.70);
    text-align: center;
    font-size: 0.92rem;
    line-height: 1.5;
  }


  .ink-death-name-line.is-survivor {
    background: linear-gradient(90deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02));
    border-bottom-color: rgba(55, 28, 8, 0.36);
  }

  .ink-death-name-input.is-survivor {
    color: #170904;
    text-shadow: 0 0 0 transparent;
  }

  .ink-death-paper-result {
    margin: 18px 0 0 38px;
    padding: 12px 12px 10px;
    border-top: 2px solid rgba(88, 18, 18, 0.24);
    background: linear-gradient(180deg, rgba(255,255,255,0.14), rgba(98,50,18,0.05));
  }

  .ink-death-paper-result-stamp {
    display: inline-block;
    margin-bottom: 10px;
    padding: 4px 8px;
    border: 2px solid rgba(95,0,0,0.56);
    color: rgba(86, 0, 0, 0.82);
    font-family: "Death Note", "Old English Text MT", Georgia, serif;
    font-size: 1rem;
    font-weight: 900;
    transform: rotate(-2deg);
    background: rgba(95,0,0,0.045);
  }

  .ink-death-paper-result-content {
    display: grid;
    grid-template-columns: 92px minmax(0, 1fr);
    gap: 14px;
    align-items: center;
  }

  .ink-death-paper-result-image,
  .ink-death-paper-result-placeholder {
    width: 92px;
    height: 92px;
    border-radius: 10px;
    object-fit: cover;
    background: rgba(43, 17, 9, 0.12);
    border: 1px solid rgba(55,21,8,0.32);
    box-shadow: 0 8px 16px rgba(54,25,8,0.18), inset 0 0 18px rgba(80,0,0,0.10);
    filter: sepia(0.14) contrast(1.02) saturate(0.9);
  }

  .ink-death-paper-result-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: "Death Note", Georgia, serif;
    font-size: 2.8rem;
    color: rgba(61, 0, 0, 0.55);
  }

  .ink-death-paper-result-info {
    min-width: 0;
  }

  .ink-death-paper-result-info span {
    display: block;
    color: rgba(74, 0, 0, 0.76);
    font-family: Inter, system-ui, sans-serif;
    font-size: 0.72rem;
    font-weight: 950;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 5px;
  }

  .ink-death-paper-result-info strong {
    display: block;
    color: #220905;
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(1.35rem, 4vw, 2rem);
    line-height: 0.95;
    font-weight: 950;
    text-transform: uppercase;
    word-break: break-word;
  }

  .ink-death-paper-result-info em {
    display: block;
    margin-top: 7px;
    color: rgba(36, 17, 9, 0.68);
    font-family: Inter, system-ui, sans-serif;
    font-size: 0.85rem;
    font-style: normal;
    font-weight: 850;
    word-break: break-word;
  }

  .ink-death-paper-discount-box {
    margin-top: 12px;
    padding: 12px 12px 10px;
    border: 2px solid rgba(115, 0, 0, 0.42);
    background:
      linear-gradient(180deg, rgba(122, 0, 0, 0.10), rgba(122, 0, 0, 0.03)),
      rgba(255,255,255,0.10);
    box-shadow:
      inset 0 0 18px rgba(255,255,255,0.10),
      0 4px 14px rgba(90, 0, 0, 0.12);
  }

  .ink-death-paper-discount-label {
    display: block;
    color: rgba(88, 0, 0, 0.86);
    font-family: Inter, system-ui, sans-serif;
    font-size: 0.72rem;
    font-weight: 950;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .ink-death-paper-discount-value {
    color: #8f0000;
    font-family: "Death Note", Georgia, serif;
    font-size: clamp(2rem, 5vw, 2.8rem);
    line-height: 0.9;
    font-weight: 900;
    letter-spacing: 0.04em;
    text-shadow:
      0 1px 0 rgba(255,255,255,0.18),
      0 0 10px rgba(143,0,0,0.18);
  }

  .ink-death-paper-price-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 8px;
    font-family: Inter, system-ui, sans-serif;
  }

  .ink-death-paper-price-row s {
    color: rgba(60, 24, 10, 0.62);
    font-size: 0.92rem;
    font-weight: 800;
    text-decoration-thickness: 2px;
  }

  .ink-death-paper-price-row strong {
    color: #1e0904;
    font-family: Inter, system-ui, sans-serif;
    font-size: 1.18rem;
    font-weight: 950;
  }

  @keyframes inkBloodSlash {
    0% { transform: scaleX(0) skewX(-8deg); opacity: 0; filter: saturate(1.45); }
    10% { opacity: 1; }
    68% { transform: scaleX(1.03) skewX(-8deg); opacity: 1; }
    100% { transform: scaleX(1) skewX(-8deg); opacity: 0.96; filter: saturate(1.05); }
  }

  @keyframes inkBloodScratch {
    0% { transform: scaleX(0) rotate(0.8deg); opacity: 0; }
    18% { opacity: 0.9; }
    100% { transform: scaleX(1) rotate(0.8deg); opacity: 0.88; }
  }

  @keyframes inkBloodMist {
    0% { opacity: 0; transform: scale(0.78) translateX(-10px); }
    45% { opacity: 1; }
    100% { opacity: 0.82; transform: scale(1) translateX(0); }
  }

  @keyframes inkBloodPop {
    0% { opacity: 0; transform: scale(0) translateY(0); }
    42% { opacity: 0.95; transform: scale(1.25) translateY(-2px); }
    100% { opacity: 0.78; transform: scale(1) translateY(2px); }
  }

  @keyframes inkBloodDrip {
    0% { height: 0; opacity: 0; transform: translateY(-2px) scaleY(0.2); }
    18% { opacity: 0.96; }
    72% { height: 22px; transform: translateY(0) scaleY(1); }
    100% { height: 28px; opacity: 0.88; transform: translateY(0) scaleY(1); }
  }

  @keyframes deathPenMove {
    from { transform: translateX(-22px) translateY(8px) rotate(-24deg); }
    to { transform: translateX(26px) translateY(-10px) rotate(-24deg); }
  }

  @keyframes deathBookShake {
    0% { transform: translate(0,0) rotate(0deg); }
    25% { transform: translate(1px,-1px) rotate(-0.2deg); }
    50% { transform: translate(-1px,1px) rotate(0.2deg); }
    75% { transform: translate(1px,1px) rotate(0deg); }
    100% { transform: translate(0,0) rotate(0deg); }
  }

  .ink-wishlist-closed-wrap {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 320px;
  }

  .ink-wishlist-closed-card {
    width: 100%;
    max-width: 760px;
    text-align: center;
    padding: 36px 24px;
    border-radius: 24px;
    background: rgba(16, 16, 16, 0.92);
    border: 1px solid rgba(255,255,255,0.10);
    box-shadow: 0 18px 60px rgba(0,0,0,0.34);
  }

  .ink-wishlist-closed-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    padding: 8px 14px;
    border-radius: 999px;
    background: rgba(255, 59, 59, 0.14);
    border: 1px solid rgba(255, 59, 59, 0.28);
    color: #ff7b7b;
    font-size: 0.82rem;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .ink-wishlist-closed-title {
    margin: 0;
    font-size: clamp(1.6rem, 4vw, 2.5rem);
    line-height: 1.05;
    font-weight: 900;
    color: #fff;
  }

  .ink-wishlist-closed-title span {
    color: #ff5b5b;
    text-shadow:
      0 0 10px rgba(255, 91, 91, 0.45),
      0 0 22px rgba(255, 91, 91, 0.28);
  }

  .ink-wishlist-closed-text {
    margin: 14px 0 0;
    color: rgba(255,255,255,0.76);
    font-size: 1rem;
    line-height: 1.5;
  }

  .ink-wishlist-hidden {
    display: none !important;
  }

  .ink-public-switch {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
    margin: 0 0 18px;
  }

  .ink-public-switch-top {
    flex: 1 1 auto;
    justify-content: flex-start;
    align-items: center;
    margin: 0;
    min-width: 0;
  }

  .ink-public-switch-top .ink-public-switch-btn {
    padding: 9px 14px;
    border-radius: 13px;
    font-size: 0.88rem;
  }

  .ink-top-home-btn {
    flex: 0 0 auto;
    padding: 9px 14px;
    border-radius: 13px;
    font-size: 0.88rem;
    min-width: 92px;
  }

  .ink-public-switch-btn {
    border: 1px solid rgba(255,255,255,0.14);
    background: rgba(255,255,255,0.06);
    color: #fff;
    border-radius: 14px;
    padding: 12px 18px;
    font-weight: 800;
    cursor: pointer;
    transition: all .18s ease;
    box-shadow: 0 10px 25px rgba(0,0,0,0.18);
  }

  .ink-public-switch-btn:hover {
    transform: translateY(-1px);
  }

  .ink-public-switch-btn.active {
    background: linear-gradient(135deg, #ff5b5b 0%, #d93a3a 100%);
    color: #fff;
    border-color: rgba(255,91,91,0.45);
    box-shadow: 0 14px 35px rgba(255,91,91,0.22);
  }

  .ink-public-block {
    width: 100%;
  }

  .ink-course-wrap {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }


  .ink-projects-wrap {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .ink-projects-hero {
    text-align: center;
    background: linear-gradient(135deg, rgba(255,91,91,0.12), rgba(255,255,255,0.04));
    border: 1px solid rgba(255,91,91,0.20);
    border-radius: 22px;
    padding: 24px 18px;
  }

  .ink-projects-kicker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 999px;
    background: rgba(255,91,91,0.16);
    border: 1px solid rgba(255,91,91,0.20);
    color: #ffd1d1;
    font-size: 0.86rem;
    font-weight: 800;
    margin-bottom: 12px;
  }

  .ink-projects-hero h2 {
    margin: 0 0 12px;
    font-size: clamp(1.9rem, 4.5vw, 3rem);
    line-height: 1.05;
    color: #ffffff;
    font-weight: 950;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-shadow:
      0 0 8px rgba(255, 91, 91, 0.65),
      0 0 18px rgba(255, 91, 91, 0.35),
      0 2px 0 rgba(0, 0, 0, 0.85);
  }

  .ink-projects-hero p {
    margin: 0;
    color: rgba(255,255,255,0.78);
    font-size: 1rem;
    line-height: 1.55;
  }

  .ink-project-section {
    display: flex;
    flex-direction: column;
    gap: 14px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 22px;
    padding: 18px;
  }

  .ink-project-section-head {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .ink-project-section-kicker {
    display: inline-flex;
    width: fit-content;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 999px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.12);
    color: #fff;
    font-size: 0.8rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .ink-project-section-head h3 {
    margin: 0;
    font-size: 1.55rem;
    line-height: 1.05;
  }

  .ink-project-section-head p {
    margin: 0;
    color: rgba(255,255,255,0.72);
    line-height: 1.5;
  }

  .ink-project-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
  }

  .ink-project-card {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 18px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.09);
    box-shadow: 0 12px 28px rgba(0,0,0,0.22);
  }

  .ink-project-card-image {
    aspect-ratio: 4 / 5;
    width: 100%;
    background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
    overflow: hidden;
  }

  .ink-project-card-image img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: var(--project-image-position, top center);
    transform: scale(1.13);
    transform-origin: top center;
  }

  .ink-project-card-placeholder {
    width: 100%;
    height: 100%;
    min-height: 210px;
    display: flex;
    align-items: center;
    justify-content: center;
    background:
      radial-gradient(circle at top, rgba(255,91,91,0.18), transparent 48%),
      linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.025));
    color: rgba(255,255,255,0.92);
  }

  .ink-project-card-placeholder strong {
    width: 64px;
    height: 64px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255,255,255,0.18);
    background: rgba(0,0,0,0.24);
    font-size: 1.1rem;
    font-weight: 950;
    letter-spacing: 0.06em;
  }

  .ink-project-card-placeholder-hidden {
    display: none;
  }

  .ink-project-card-image.is-image-error .ink-project-card-placeholder-hidden {
    display: flex;
  }

  .ink-project-card-api-status {
    margin-top: 2px;
    color: rgba(255,255,255,0.50);
    font-size: 0.76rem;
    font-weight: 700;
  }

  .ink-project-whatsapp-btn {
    width: 100%;
    margin-top: 8px;
    border-radius: 13px;
    padding: 10px 11px;
    font-size: 0.82rem;
    font-weight: 900;
    text-align: center;
    text-decoration: none;
    color: #fff;
    background: linear-gradient(135deg, #25d366 0%, #179f4c 100%);
    box-shadow: 0 10px 24px rgba(37,211,102,0.20);
  }

  .ink-project-whatsapp-btn:hover {
    transform: translateY(-1px);
  }

  .ink-project-card-body {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .ink-project-card-body h4 {
    margin: 0;
    font-size: 1rem;
    line-height: 1.15;
  }

  .ink-project-card-body span {
    color: rgba(255,255,255,0.62);
    font-size: 0.88rem;
  }

  .ink-project-empty {
    border-radius: 18px;
    border: 1px dashed rgba(255,255,255,0.16);
    background: rgba(255,255,255,0.03);
    padding: 22px 16px;
    text-align: center;
    color: rgba(255,255,255,0.76);
    line-height: 1.55;
  }

  .ink-course-hero {
    text-align: center;
    background: linear-gradient(135deg, rgba(255,91,91,0.12), rgba(255,255,255,0.04));
    border: 1px solid rgba(255,91,91,0.20);
    border-radius: 22px;
    padding: 22px 18px;
  }

  .ink-course-kicker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 999px;
    background: rgba(255,91,91,0.12);
    border: 1px solid rgba(255,91,91,0.24);
    color: #ff7b7b;
    font-size: 0.84rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  .ink-course-hero h2 {
    margin: 0 0 10px;
    font-size: clamp(1.5rem, 3vw, 2.2rem);
    color: #fff;
  }

  .ink-course-hero p {
    margin: 0;
    color: rgba(255,255,255,0.82);
    line-height: 1.6;
    font-size: 1rem;
  }

  .ink-course-metrics {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
  }

  .ink-course-metric {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 18px;
    padding: 16px 12px;
    text-align: center;
  }

  .ink-course-metric strong {
    display: block;
    font-size: 1.5rem;
    color: #ff7b7b;
    margin-bottom: 6px;
  }

  .ink-course-metric span {
    color: rgba(255,255,255,0.82);
    font-size: 0.92rem;
    line-height: 1.35;
  }

  .ink-course-section {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 20px;
    padding: 18px;
  }

  .ink-course-section h3 {
    margin: 0 0 12px;
    color: #fff;
    font-size: 1.08rem;
  }

  .ink-course-section p {
    margin: 0;
    color: rgba(255,255,255,0.82);
    line-height: 1.65;
  }

  .ink-course-list {
    margin: 0;
    padding-left: 18px;
    color: rgba(255,255,255,0.84);
    line-height: 1.7;
  }

  .ink-course-list li + li {
    margin-top: 4px;
  }

  .ink-course-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .ink-course-group {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 18px;
    padding: 16px;
  }

  .ink-course-group h4 {
    margin: 0 0 10px;
    color: #ff7b7b;
    font-size: 1rem;
  }

  .ink-course-pricing {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;
  }

  .ink-course-price-card {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 18px;
    padding: 16px;
    text-align: center;
  }

  .ink-course-price-card span {
    display: block;
    color: rgba(255,255,255,0.72);
    font-size: 0.9rem;
    margin-bottom: 6px;
  }

  .ink-course-price-card strong {
    color: #fff;
    font-size: 1.15rem;
  }

  .ink-course-cta-row {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 14px;
  }

  .ink-course-note {
    text-align: center;
    margin: 14px 0 0;
    color: rgba(255,255,255,0.72);
    font-size: 0.92rem;
  }


  .ink-home-hero {
    position: relative;
    overflow: visible;
    border-radius: 28px;
    border: 1px solid rgba(255,255,255,0.08);
    background: transparent;
    padding: 34px;
    box-shadow: none;
  }

  .ink-home-grid {
    position: relative;
    z-index: 2;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 22px;
  }

  .ink-home-glow {
    position: absolute;
    border-radius: 999px;
    filter: blur(80px);
    pointer-events: none;
  }

  .ink-home-glow-1 {
    width: 260px;
    height: 260px;
    right: -80px;
    top: -40px;
    background: rgba(255, 0, 0, 0.08);
  }

  .ink-home-glow-2 {
    width: 220px;
    height: 220px;
    left: -70px;
    bottom: 20px;
    background: rgba(255, 255, 255, 0.05);
  }

  .ink-home-grid-banner {
    width: 100%;
  }

  .ink-home-closed-banner {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 18px 20px;
    border-radius: 20px;
    border: 1px solid rgba(255, 91, 91, 0.24);
    background: rgba(16, 16, 16, 0.92);
    box-shadow: 0 10px 30px rgba(0,0,0,0.16);
  }

  .ink-home-closed-banner span {
    color: #ff5b5b;
    font-size: 0.8rem;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-shadow:
      0 0 10px rgba(255, 91, 91, 0.45),
      0 0 22px rgba(255, 91, 91, 0.28);
  }

  .ink-home-closed-banner strong {
    color: #fff;
    font-size: 1rem;
    line-height: 1.3;
    text-shadow:
      0 0 10px rgba(255, 91, 91, 0.18),
      0 0 18px rgba(255, 91, 91, 0.12);
  }

  .ink-home-closed-banner-open {
    border-color: rgba(255,91,91,0.26);
    background: rgba(255,91,91,0.10);
  }

  .ink-home-closed-banner-open span {
    color: #ff7b7b;
  }

  .ink-home-video-placeholder-wrap {
    width: 100%;
  }

  .ink-home-video-placeholder {
    position: relative;
    width: 100%;
    min-height: 300px;
    border-radius: 24px;
    border: 1px solid rgba(255,255,255,0.08);
    background:
      radial-gradient(circle at top, rgba(255,255,255,0.06), transparent 30%),
      linear-gradient(180deg, rgba(0,0,0,0.52) 0%, rgba(8,10,14,0.94) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0,0,0,0.22);
  }

  .ink-home-video-placeholder::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0.00)),
      radial-gradient(circle at center, rgba(255,255,255,0.05), transparent 45%);
  }

  .ink-home-video-play {
    position: relative;
    z-index: 1;
    width: 88px;
    height: 88px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.14);
    background: rgba(255,255,255,0.08);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: #fff;
    box-shadow: 0 18px 45px rgba(0,0,0,0.30);
  }

  .ink-home-video-note {
    position: absolute;
    left: 20px;
    bottom: 18px;
    z-index: 1;
    color: rgba(255,255,255,0.70);
    font-size: 0.92rem;
    letter-spacing: 0.02em;
  }

  .ink-home-bottom {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
    gap: 22px;
    align-items: stretch;
  }

  .ink-home-copy-card,
  .ink-home-summary-card,
  .ink-home-stat {
    border: 1px solid rgba(255,255,255,0.10);
    background: rgba(255,255,255,0.04);
    backdrop-filter: blur(14px);
    box-shadow: 0 20px 60px rgba(0,0,0,0.22);
  }

  .ink-home-copy-card,
  .ink-home-summary-card {
    border-radius: 26px;
    padding: 24px;
  }

  .ink-home-title {
    margin: 0;
    font-size: clamp(2rem, 4.5vw, 4rem);
    line-height: 1.02;
    letter-spacing: -0.03em;
    max-width: 760px;
  }

  .ink-home-text {
    color: rgba(255,255,255,0.66);
    line-height: 1.8;
    font-size: 1rem;
    max-width: 720px;
    margin-top: 16px;
  }

  .ink-home-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;
  }

  .ink-home-tags span {
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.1);
    background: rgba(255,255,255,0.05);
    padding: 11px 15px;
    color: rgba(255,255,255,0.74);
    font-size: 0.92rem;
  }

  .ink-home-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-top: 28px;
    align-items: center;
  }

  .ink-home-actions .ink-btn {
    width: auto;
    min-width: 190px;
  }

  .ink-home-summary-card {
    display: grid;
    gap: 14px;
    align-content: start;
    background: linear-gradient(135deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03));
  }

  .ink-home-summary-item {
    border-radius: 20px;
    padding: 16px 18px;
    border: 1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.04);
  }

  .ink-home-summary-item strong {
    display: block;
    color: #fff;
    font-size: 1rem;
    margin-bottom: 8px;
  }

  .ink-home-summary-item span {
    display: block;
    color: rgba(255,255,255,0.64);
    line-height: 1.6;
    font-size: 0.92rem;
  }

  .ink-home-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }

  .ink-home-stat {
    border-radius: 24px;
    padding: 20px;
  }

  .ink-home-stat strong {
    display: block;
    font-size: 1.8rem;
    margin-bottom: 8px;
    color: #fff;
  }

  .ink-home-stat p {
    margin: 0;
    color: rgba(255,255,255,0.6);
    font-size: 0.92rem;
    line-height: 1.6;
  }

  .ink-home-mini-card-wide {
    grid-column: 1 / -1;
  }


  @media (max-width: 900px) {
    .ink-project-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 560px) {
    .ink-project-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 8px;
    }

    .ink-project-section {
      padding: 12px;
    }

    .ink-project-card {
      border-radius: 13px;
    }

    .ink-project-card-image {
      aspect-ratio: 1 / 1.12;
    }

    .ink-project-card-body {
      padding: 8px 6px;
      gap: 2px;
    }

    .ink-project-card-body h4 {
      font-size: 0.76rem;
      line-height: 1.05;
    }

    .ink-project-card-body span {
      font-size: 0.66rem;
    }

    .ink-project-card-api-status {
      display: none;
    }

    .ink-project-whatsapp-btn {
      margin-top: 6px;
      padding: 8px 6px;
      border-radius: 10px;
      font-size: 0.63rem;
      line-height: 1.05;
    }
  }

  @media (max-width: 900px) {
    .ink-home-grid,
    .ink-course-metrics,
    .ink-course-pricing,
    .ink-course-grid {
      grid-template-columns: 1fr 1fr;
    }

    .ink-home-bottom,
    .ink-home-grid {
      grid-template-columns: 1fr;
    }

    .ink-home-stats {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 640px) {
    .ink-public-switch {
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: stretch;
      gap: 10px;
    }

    .ink-enter-top {
      gap: 8px;
      align-items: center;
      margin-bottom: 8px;
    }

    .ink-public-switch-top {
      flex: 1 1 auto;
      overflow-x: visible;
      justify-content: flex-start;
      padding-bottom: 2px;
      scrollbar-width: none;
      flex-wrap: wrap;
    }

    .ink-public-switch-top::-webkit-scrollbar {
      display: none;
    }

    .ink-public-switch-top .ink-public-switch-btn {
      flex: 0 0 auto;
      padding: 9px 11px;
      font-size: 0.78rem;
    }

    .ink-home-hero {
      padding: 22px 18px;
    }

    .ink-home-bottom {
      gap: 18px;
    }

    .ink-home-copy-card,
    .ink-home-summary-card {
      padding: 20px;
    }

    .ink-home-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .ink-home-actions .ink-btn {
      width: 100%;
      min-width: 0;
    }

    .ink-public-switch-btn {
      flex: 1 1 0;
      width: auto;
      min-width: 0;
      padding: 12px 8px;
      font-size: 0.82rem;
      white-space: nowrap;
      letter-spacing: -0.01em;
    }

    .ink-course-metrics {
      grid-template-columns: 1fr 1fr;
    }

    .ink-course-pricing,
    .ink-course-grid {
      grid-template-columns: 1fr;
    }
  }


  @media (max-width: 940px) {
    .ink-death-hero-grid {
      grid-template-columns: 1fr;
    }

    .ink-death-book {
      min-height: 340px;
    }

    .ink-death-page {
      min-height: 310px;
    }
  }

  @media (max-width: 640px) {
    .ink-death-shell {
      padding: 16px;
      border-radius: 24px;
    }

    .ink-death-title {
      font-size: clamp(3.2rem, 17vw, 4.8rem);
      line-height: 0.76;
    }

    .ink-death-page {
      padding: 88px 10px 16px;
    }

    .ink-death-page::before {
      left: 34px;
    }

    .ink-death-name-line {
      padding-left: 30px;
      min-height: 60px;
    }

    .ink-death-name-input {
      font-size: 1.22rem;
    }

    .ink-death-name-number {
      left: 2px;
    }

    .ink-death-paper-result {
      margin-left: 30px;
      padding: 10px;
    }

    .ink-death-paper-result-content {
      grid-template-columns: 1fr;
    }

    .ink-death-paper-result-image,
    .ink-death-paper-result-placeholder {
      width: 84px;
      height: 84px;
    }

    .ink-death-book::before {
      content: none;
      display: none;
    }
  }

  @media (max-width: 860px) {
    .ink-fields-grid,
    .ink-reserve-grid {
      grid-template-columns: 1fr;
    }

    .ink-main-card {
      padding: 18px;
      border-radius: 22px;
    }


    .ink-main-card-death {
      border-radius: 24px;
    }

    .ink-enter-top {
      justify-content: flex-end;
      margin-bottom: 8px;
    }

    .ink-btn-outline {
      width: 90px;
      min-width: 90px;
      padding: 8px 10px;
      font-size: 0.88rem;
    }

    .ink-result-character {
      flex-direction: column;
      text-align: center;
    }

    .ink-result-image {
      width: 148px;
      height: 148px;
    }

    .ink-brand-main {
      font-size: clamp(6.6rem, 14vw, 5rem);
    }

    .ink-brand-sub {
      font-size: clamp(1.4rem, 7vw, 2.4rem);
    }

    .ink-result-cta,
    .ink-pix-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .ink-pix-qr {
      width: 190px;
      height: 190px;
    }
  }


  /* RODRIGO: SCROLL CERTO.
     Somente a página/body rola. Nenhum card cria rolagem própria. */
  html {
    width: 100% !important;
    min-height: 100% !important;
    height: auto !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    overscroll-behavior-y: auto !important;
  }

  body {
    width: 100% !important;
    min-height: 100vh !important;
    height: auto !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    position: static !important;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: auto !important;
    touch-action: pan-y !important;
  }

  #root {
    width: 100% !important;
    min-height: 100vh !important;
    height: auto !important;
    overflow: visible !important;
    overflow-x: hidden !important;
    overflow-y: visible !important;
  }

  .ink-login-screen {
    min-height: 100vh !important;
    height: auto !important;
    overflow: visible !important;
    overflow-x: hidden !important;
    overflow-y: visible !important;
    padding-bottom: 140px !important;
    touch-action: pan-y !important;
  }

  .ink-login-shell,
  .ink-main-card,
  .ink-main-card-death,
  .ink-main-card-inner,
  .ink-public-block,
  .ink-course-wrap,
  .ink-death-shell,
  .ink-death-book,
  .ink-death-page,
  .ink-home-hero {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
  }

  .ink-suggestions {
    max-height: none !important;
    overflow: visible !important;
    overflow-y: visible !important;
  }

  .ink-death-suggestions {
    max-height: min(260px, 45vh) !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }

  .ink-modal-overlay {
    overflow: visible !important;
    overflow-y: visible !important;
  }

  .ink-modal {
    max-height: none !important;
    overflow: visible !important;
    overflow-y: visible !important;
  }


  .ink-death-only-actions {
    position: relative;
    z-index: 1;
    margin-top: 14px;
    margin-bottom: 0;
  }

  .ink-death-only-result-buttons {
    margin-top: 0;
  }


  .ink-d20-roll-btn {
    position: relative;
    min-width: 260px;
    min-height: 58px;
    padding: 10px 22px 10px 14px;
    gap: 12px;
    overflow: hidden;
  }

  .ink-d20-roll-btn::before {
    content: '';
    position: absolute;
    inset: -80px;
    background:
      radial-gradient(circle at 18% 18%, rgba(255,255,255,0.22), transparent 22%),
      radial-gradient(circle at 82% 78%, rgba(0,0,0,0.20), transparent 34%);
    opacity: 0.35;
    pointer-events: none;
  }

  .ink-d20-die {
    position: relative;
    z-index: 1;
    width: 48px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 48px;
    color: #fff;
    font-size: 1rem;
    font-weight: 950;
    line-height: 1;
    text-shadow: 0 2px 5px rgba(0,0,0,0.72);
    clip-path: polygon(50% 0%, 3% 100%, 97% 100%);
    background:
      linear-gradient(180deg, rgba(255,255,255,0.28), transparent 30%),
      linear-gradient(145deg, #d90000 0%, #930000 48%, #320000 100%);
    border: 1px solid rgba(255,255,255,0.22);
    box-shadow:
      inset 0 0 0 2px rgba(0,0,0,0.20),
      inset 8px 10px 12px rgba(255,255,255,0.10),
      inset -10px -12px 18px rgba(0,0,0,0.50),
      0 12px 26px rgba(0,0,0,0.40),
      0 0 26px rgba(255,0,0,0.22);
    transform: translateY(1px);
  }

  .ink-d20-die::before,
  .ink-d20-die::after {
    content: '';
    position: absolute;
    pointer-events: none;
    clip-path: polygon(50% 0%, 5% 100%, 95% 100%);
  }

  .ink-d20-die::before {
    inset: 6px;
    border: 1px solid rgba(255,255,255,0.24);
    opacity: 0.38;
  }

  .ink-d20-die::after {
    inset: 12px 13px 7px;
    border-top: 1px solid rgba(255,255,255,0.22);
    opacity: 0.22;
  }

  .ink-d20-die.is-rolling {
    animation: inkD20Roll 0.18s linear infinite;
  }

  .ink-d20-btn-text {
    position: relative;
    z-index: 1;
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.05;
    text-align: left;
  }

  .ink-d20-btn-text strong {
    font-size: 0.98rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .ink-d20-btn-text small {
    margin-top: 4px;
    font-size: 0.72rem;
    opacity: 0.78;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  @keyframes inkD20Roll {
    0% { transform: rotate(-8deg) translateY(0) scale(1); filter: brightness(1); }
    25% { transform: rotate(18deg) translateY(-3px) scale(1.06); filter: brightness(1.18); }
    50% { transform: rotate(84deg) translateY(1px) scale(0.98); filter: brightness(0.96); }
    75% { transform: rotate(146deg) translateY(-2px) scale(1.04); filter: brightness(1.12); }
    100% { transform: rotate(352deg) translateY(0) scale(1); filter: brightness(1); }
  }


  .ink-music-toggle {
    position: fixed;
    right: 14px;
    bottom: 14px;
    z-index: 2000;
    border: 1px solid rgba(255,255,255,0.16);
    background: rgba(10,10,10,0.82);
    color: #fff;
    border-radius: 999px;
    padding: 10px 14px;
    font-size: 0.86rem;
    font-weight: 900;
    cursor: pointer;
    box-shadow: 0 12px 34px rgba(0,0,0,0.42);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease, opacity .18s ease;
  }

  .ink-music-toggle:hover {
    transform: translateY(-1px);
  }

  .ink-music-toggle.is-on {
    border-color: rgba(255,91,91,0.42);
    box-shadow:
      0 12px 34px rgba(0,0,0,0.42),
      0 0 18px rgba(255,91,91,0.22);
  }

  @media (max-width: 520px) {
    .ink-music-toggle {
      right: 10px;
      bottom: 10px;
      padding: 9px 12px;
      font-size: 0.8rem;
    }
  }

`;

const initialCharacterSlot = () => ({
  query: '',
  selected: null,
  suggestions: [],
  loading: false,
  open: false,
  error: '',
});

const STATIC_GAME_CHARACTERS = [
  { id: 'genshin-1', name: 'Aether', subtitle: 'Genshin Impact', image: 'https://api.genshin.dev/characters/traveler-anemo/icon-big' },
  { id: 'genshin-2', name: 'Lumine', subtitle: 'Genshin Impact', image: 'https://api.genshin.dev/characters/traveler-geo/icon-big' },
  { id: 'genshin-3', name: 'Diluc', subtitle: 'Genshin Impact', image: 'https://api.genshin.dev/characters/diluc/icon-big' },
  { id: 'genshin-4', name: 'Raiden Shogun', subtitle: 'Genshin Impact', image: 'https://api.genshin.dev/characters/raiden-shogun/icon-big' },
  { id: 'genshin-5', name: 'Zhongli', subtitle: 'Genshin Impact', image: 'https://api.genshin.dev/characters/zhongli/icon-big' },
  { id: 'genshin-6', name: 'Xiao', subtitle: 'Genshin Impact', image: 'https://api.genshin.dev/characters/xiao/icon-big' },
  { id: 'genshin-7', name: 'Nahida', subtitle: 'Genshin Impact', image: 'https://api.genshin.dev/characters/nahida/icon-big' },
  { id: 'genshin-8', name: 'Furina', subtitle: 'Genshin Impact', image: 'https://api.genshin.dev/characters/furina/icon-big' },
  { id: 'pokemon-1', name: 'Pikachu', subtitle: 'Pokémon', image: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png' },
  { id: 'pokemon-2', name: 'Charizard', subtitle: 'Pokémon', image: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/6.png' },
  { id: 'pokemon-3', name: 'Mewtwo', subtitle: 'Pokémon', image: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/150.png' },
  { id: 'pokemon-4', name: 'Lucario', subtitle: 'Pokémon', image: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/448.png' },
  { id: 'pokemon-5', name: 'Gengar', subtitle: 'Pokémon', image: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/94.png' },
  { id: 'pokemon-6', name: 'Greninja', subtitle: 'Pokémon', image: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/658.png' },
  { id: 'pokemon-7', name: 'Eevee', subtitle: 'Pokémon', image: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/133.png' },
  { id: 'pokemon-8', name: 'Gardevoir', subtitle: 'Pokémon', image: 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/282.png' },
];

export default function LoginScreen() {
  const [publicView, setPublicView] = useState('inicio');
  const [apiGeekProjects, setApiGeekProjects] = useState(() =>
    GEEK_PROJECTS.map((project) => ({
      ...project,
      image: '',
      apiStatus: 'loading',
      apiSource: '',
    }))
  );
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const [characterSlots, setCharacterSlots] = useState([
    initialCharacterSlot(),
    initialCharacterSlot(),
    initialCharacterSlot(),
  ]);
  const [raffleLoading, setRaffleLoading] = useState(false);
  const [raffleMessage, setRaffleMessage] = useState('');
  const [d20Value, setD20Value] = useState(3);
  const [d20Rolling, setD20Rolling] = useState(false);
  const [d20Result, setD20Result] = useState(null);
  const [winnerCharacter, setWinnerCharacter] = useState(null);
  const [winnerCharacterImage, setWinnerCharacterImage] = useState('');
  const [eliminatedSlots, setEliminatedSlots] = useState([]);
  const [selectionLocked, setSelectionLocked] = useState(false);

  const [pokemonList, setPokemonList] = useState([]);
  const [pokemonLoaded, setPokemonLoaded] = useState(false);

  const searchTimersRef = useRef({});

  const bgAudioRef = useRef(null);
  const musicStartedRef = useRef(false);
  const [musicOn, setMusicOn] = useState(false);

  const playSiteMusic = async () => {
    const audio = bgAudioRef.current;
    if (!audio) return false;

    try {
      audio.volume = 0.28;
      audio.loop = true;
      audio.muted = false;

      await audio.play();

      musicStartedRef.current = true;
      setMusicOn(true);
      return true;
    } catch (error) {
      musicStartedRef.current = false;
      setMusicOn(false);
      console.warn('Autoplay com som foi bloqueado pelo navegador. A música será liberada no primeiro clique/toque do visitante.', error);
      return false;
    }
  };

  const toggleSiteMusic = async () => {
    const audio = bgAudioRef.current;
    if (!audio) return;

    if (audio.paused) {
      await playSiteMusic();
      return;
    }

    audio.pause();
    musicStartedRef.current = false;
    setMusicOn(false);
  };


  const currencyBRL = useMemo(
    () =>
      new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }),
    []
  );

  const showDeathNoteWishlist = !WISHLIST_CLOSED && DEATH_NOTE_PREVIEW_ACTIVE;


  useEffect(() => {
    if (WISHLIST_CLOSED || !DEATH_NOTE_PREVIEW_ACTIVE) return undefined;

    const audio = bgAudioRef.current;
    if (!audio) return undefined;

    audio.volume = 0.28;
    audio.loop = true;
    audio.muted = false;

    const tryAutoplay = () => {
      if (!musicStartedRef.current) {
        playSiteMusic();
      }
    };

    // Tenta tocar automaticamente ao entrar no site.
    // Alguns navegadores bloqueiam autoplay com som; nesses casos, libera no primeiro clique/toque/tecla.
    tryAutoplay();

    window.addEventListener('click', tryAutoplay, { once: true });
    window.addEventListener('touchstart', tryAutoplay, { once: true });
    window.addEventListener('keydown', tryAutoplay, { once: true });

    return () => {
      window.removeEventListener('click', tryAutoplay);
      window.removeEventListener('touchstart', tryAutoplay);
      window.removeEventListener('keydown', tryAutoplay);
    };
  }, []);

  useEffect(() => {
    if (WISHLIST_CLOSED || !DEATH_NOTE_PREVIEW_ACTIVE) {
      setPokemonLoaded(true);
      return undefined;
    }

    let ignore = false;

    const preloadPokemon = async () => {
      try {
        const response = await fetch('https://pokeapi.co/api/v2/pokemon?limit=2000');
        const data = await response.json();

        if (ignore) return;

        const list = (data?.results || []).map((item, index) => ({
          id: `pokemon-api-${item.name}`,
          name: formatSlugName(item.name),
          subtitle: 'Pokémon',
          image: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${index + 1}.png`,
        }));

        setPokemonList(list);
        setPokemonLoaded(true);
      } catch (error) {
        console.error('Erro ao carregar PokéAPI:', error);
        setPokemonLoaded(true);
      }
    };

    preloadPokemon();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (WISHLIST_CLOSED || !DEATH_NOTE_PREVIEW_ACTIVE) return undefined;

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return;

      const parsed = JSON.parse(saved);

      if (parsed?.locked) {
        if (Array.isArray(parsed.characterSlots) && parsed.characterSlots.length === 3) {
          setCharacterSlots(
            parsed.characterSlots.map((slot) => ({
              ...initialCharacterSlot(),
              query: slot?.query || '',
              selected: slot?.selected || null,
              suggestions: [],
              loading: false,
              open: false,
              error: '',
            }))
          );
        }

        if (parsed?.winnerCharacter) {
          setWinnerCharacter(parsed.winnerCharacter);
          setWinnerCharacterImage(parsed.winnerCharacter.image || '');
        }

        if (Array.isArray(parsed?.eliminatedSlots)) {
          setEliminatedSlots(parsed.eliminatedSlots);
        }

        if (parsed?.d20Result) {
          setD20Result(parsed.d20Result);
          setD20Value(parsed.d20Result);
        }

        setSelectionLocked(true);
      }
    } catch (error) {
      console.error('Erro ao restaurar lock da wishlist:', error);
    }
  }, []);

  const persistLockedState = (slots, winner, rollValue, eliminated) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        locked: true,
        characterSlots: slots.map((slot) => ({
          query: slot.query,
          selected: slot.selected,
        })),
        winnerCharacter: winner,
        eliminatedSlots: eliminated,
        d20Result: rollValue,
      })
    );
  };

  const getLocalDeviceId = () => {
    const existingId = localStorage.getItem('inkrodriguez_device_id');

    if (existingId) return existingId;

    const newId = `${Date.now()}_${Math.random().toString(36).slice(2)}_${navigator.userAgent.length}`;
    localStorage.setItem('inkrodriguez_device_id', newId);
    return newId;
  };

  const traduzirErroAuth = (code) => {
    switch (code) {
      case 'auth/invalid-email':
        return 'E-mail inválido.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'E-mail ou senha incorretos.';
      case 'auth/too-many-requests':
        return 'Muitas tentativas. Tente novamente mais tarde.';
      case 'auth/network-request-failed':
        return 'Falha de conexão. Verifique sua internet.';
      default:
        return 'Não foi possível autenticar agora.';
    }
  };

  const gerarMatriculaBase = () => {
    const ano = new Date().getFullYear().toString().slice(-2);
    const numero = Math.floor(100000 + Math.random() * 900000);
    return `INK-${ano}${numero}`;
  };

  const gerarMatriculaUnica = async () => {
    let matricula = '';
    let existe = true;

    while (existe) {
      matricula = gerarMatriculaBase();

      const alunosRef = collection(db, 'alunos');
      const q = query(alunosRef, where('matricula', '==', matricula), limit(1));
      const snap = await getDocs(q);
      existe = !snap.empty;
    }

    return matricula;
  };

  const ensureAuthorizedDevice = async (currentUser, nomeFinal) => {
    if (!DISPOSITIVO_UNICO_ATIVO) {
      return { ok: true };
    }

    const currentDeviceId = getLocalDeviceId();
    const deviceRef = doc(db, 'usuarios_dispositivos_autorizados', currentUser.uid);
    const deviceSnap = await getDoc(deviceRef);
    const emailFinal = (currentUser.email || '').trim().toLowerCase();

    if (!deviceSnap.exists()) {
      await setDoc(deviceRef, {
        uid: currentUser.uid,
        nome: nomeFinal,
        email: emailFinal,
        authorizedDeviceId: currentDeviceId,
        firstUserAgent: navigator.userAgent,
        lastUserAgent: navigator.userAgent,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      });
      return { ok: true };
    }

    const deviceData = deviceSnap.data();

    if (deviceData.authorizedDeviceId !== currentDeviceId) {
      return {
        ok: false,
        message:
          'Este dispositivo não está autorizado para acessar esta conta. Faça login no dispositivo cadastrado.',
      };
    }

    await updateDoc(deviceRef, {
      nome: nomeFinal || deviceData.nome || '',
      email: emailFinal || deviceData.email || '',
      lastUserAgent: navigator.userAgent,
      lastLoginAt: serverTimestamp(),
    });

    return { ok: true };
  };

  const salvarAlunoSeNaoExistir = async (user) => {
    const alunoRef = doc(db, 'alunos', user.uid);
    const alunoSnap = await getDoc(alunoRef);

    const nomeFinal =
      user.displayName?.trim() ||
      localStorage.getItem('inkrodriguez_nome') ||
      user.email?.split('@')[0] ||
      'Aluno';

    localStorage.setItem('inkrodriguez_nome', nomeFinal);

    const authDeviceCheck = await ensureAuthorizedDevice(user, nomeFinal);
    if (!authDeviceCheck.ok) {
      return authDeviceCheck;
    }

    if (!alunoSnap.exists()) {
      const matricula = await gerarMatriculaUnica();

      await setDoc(alunoRef, {
        uid: user.uid,
        nome: nomeFinal,
        email: (user.email || '').trim().toLowerCase(),
        matricula,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(alunoRef, {
        nome: nomeFinal,
        email: (user.email || '').trim().toLowerCase(),
        updatedAt: serverTimestamp(),
      });
    }

    return { ok: true };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoadingAuth(true);
    setAuthMessage('');

    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), senha);
      const result = await salvarAlunoSeNaoExistir(cred.user);

      if (!result.ok) {
        setAuthMessage(result.message || 'Não foi possível liberar seu acesso.');
        setLoadingAuth(false);
        return;
      }

      setAuthMessage('Login realizado com sucesso.');
      setAuthModalOpen(false);
    } catch (error) {
      console.error(error);
      setAuthMessage(traduzirErroAuth(error.code));
    } finally {
      setLoadingAuth(false);
    }
  };

  const normalizeText = (value = '') =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const formatSlugName = (slug = '') =>
    slug
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const uniqueById = (items = []) => {
    const map = new Map();
    items.forEach((item) => {
      if (!item?.id) return;
      if (!map.has(item.id)) {
        map.set(item.id, item);
      }
    });
    return Array.from(map.values());
  };

  const getLocalSuggestions = (searchTerm) => {
    const normalizedSearch = normalizeText(searchTerm);
    if (!normalizedSearch) return [];

    const localPool = [...STATIC_GAME_CHARACTERS, ...pokemonList];

    return uniqueById(
      localPool.filter((item) => {
        const name = normalizeText(item.name);
        const subtitle = normalizeText(item.subtitle);
        return name.includes(normalizedSearch) || subtitle.includes(normalizedSearch);
      })
    ).slice(0, 8);
  };

  const updateSlot = (slotIndex, updates) => {
    setCharacterSlots((prev) =>
      prev.map((slot, index) =>
        index === slotIndex
          ? {
              ...slot,
              ...updates,
            }
          : slot
      )
    );
  };

  const fetchAniListSuggestions = async (search) => {
    const queryAniList = `
      query ($search: String) {
        Page(page: 1, perPage: 8) {
          characters(search: $search, sort: FAVOURITES_DESC) {
            id
            name {
              full
            }
            image {
              large
              medium
            }
            media(perPage: 1, sort: POPULARITY_DESC) {
              nodes {
                title {
                  romaji
                  english
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch('https://graphql.anilist.co', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        query: queryAniList,
        variables: { search },
      }),
    });

    if (!response.ok) {
      throw new Error('Falha ao buscar personagens na AniList.');
    }

    const data = await response.json();

    return (data?.data?.Page?.characters || []).map((character) => ({
      id: `anilist-${character.id}`,
      name: character?.name?.full || 'Personagem',
      subtitle:
        character?.media?.nodes?.[0]?.title?.romaji ||
        character?.media?.nodes?.[0]?.title?.english ||
        'Anime',
      image: character?.image?.large || character?.image?.medium || '',
    }));
  };

  const handleCharacterInputChange = (slotIndex, value) => {
    if (selectionLocked) return;

    const nextValue = value || '';

    updateSlot(slotIndex, {
      query: nextValue,
      selected: null,
      loading: nextValue.trim().length >= 2,
      suggestions: nextValue.trim().length >= 2 ? getLocalSuggestions(nextValue) : [],
      open: nextValue.trim().length >= 2,
      error: '',
    });

    if (searchTimersRef.current[slotIndex]) {
      clearTimeout(searchTimersRef.current[slotIndex]);
    }

    if (nextValue.trim().length < 2) {
      return;
    }

    searchTimersRef.current[slotIndex] = setTimeout(async () => {
      try {
        const localSuggestions = getLocalSuggestions(nextValue);
        const animeSuggestions = await fetchAniListSuggestions(nextValue);

        const merged = uniqueById([...localSuggestions, ...animeSuggestions]).slice(0, 8);

        updateSlot(slotIndex, {
          suggestions: merged,
          open: true,
          loading: false,
          error: merged.length ? '' : 'Nenhum personagem encontrado.',
        });
      } catch (error) {
        console.error(error);

        const fallbackSuggestions = getLocalSuggestions(nextValue);

        updateSlot(slotIndex, {
          suggestions: fallbackSuggestions,
          open: true,
          loading: false,
          error: fallbackSuggestions.length ? '' : 'Nenhum personagem encontrado.',
        });
      }
    }, 350);
  };

  const handleSelectSuggestion = (slotIndex, suggestion) => {
    if (selectionLocked) return;

    updateSlot(slotIndex, {
      query: suggestion.name,
      selected: suggestion,
      suggestions: [],
      open: false,
      loading: false,
      error: '',
    });
  };

  const getSelectedCharacters = () =>
    characterSlots
      .map((slot, index) => {
        if (slot.selected) {
          return {
            ...slot.selected,
            slotIndex: index,
          };
        }

        const typedName = slot.query.trim();

        if (!typedName) return null;

        return {
          id: `manual-${index}-${normalizeText(typedName).replace(/\s+/g, '-')}`,
          name: typedName,
          subtitle: 'Nome escrito manualmente',
          image: '',
          slotIndex: index,
        };
      })
      .filter(Boolean);

  const getWinnerSlotIndexByD20 = (rollValue) => {
    if (rollValue <= 1) return 0;
    if (rollValue <= 2) return 1;
    return 2;
  };

  const animateD20Roll = (finalRoll) =>
    new Promise((resolve) => {
      let elapsed = 0;
      const totalDuration = 900;
      const frameTime = 55;

      const timer = setInterval(() => {
        elapsed += frameTime;
        setD20Value(Math.floor(Math.random() * 3) + 1);

        if (elapsed >= totalDuration) {
          clearInterval(timer);
          setD20Value(finalRoll);
          resolve();
        }
      }, frameTime);
    });

  const handleSortear = async () => {
    if (selectionLocked || raffleLoading || d20Rolling) return;

    const selectedCharacters = getSelectedCharacters();

    if (selectedCharacters.length !== 3) {
      setRaffleMessage('Escreva 3 personagens nas linhas do Death Note antes de rolar o D3.');
      return;
    }

    setRaffleLoading(true);
    setD20Rolling(true);
    setD20Result(null);
    setRaffleMessage('');

    try {
      const finalRoll = Math.floor(Math.random() * 3) + 1;
      await animateD20Roll(finalRoll);

      const winnerSlotIndex = getWinnerSlotIndexByD20(finalRoll);
      const winner =
        selectedCharacters.find((character) => character.slotIndex === winnerSlotIndex) ||
        selectedCharacters[Math.floor(Math.random() * selectedCharacters.length)];
      const survivorSlotIndex = winner?.slotIndex ?? winnerSlotIndex;
      const eliminated = [0, 1, 2].filter((slotIndex) => slotIndex !== survivorSlotIndex);

      setD20Result(finalRoll);
      setWinnerCharacter(winner);
      setWinnerCharacterImage(winner?.image || '');
      setEliminatedSlots(eliminated);
      setSelectionLocked(true);
      setRaffleMessage(`O D3 caiu em ${finalRoll}. Dois nomes foram riscados e um permaneceu.`);
      persistLockedState(characterSlots, winner, finalRoll, eliminated);
    } catch (error) {
      console.error(error);
      setRaffleMessage('Não foi possível rolar o D3 agora.');
    } finally {
      setD20Rolling(false);
      setRaffleLoading(false);
    }
  };

  const buildPersonalProjectWhatsappLink = (project) => {
    const characterName = project?.name || 'personagem';
    const text = encodeURIComponent(
      `Olá! Quero garantir esse projeto pessoal do personagem ${characterName}.\n\nVi no site que esse projeto está com 50% de desconto e quero falar sobre essa tattoo.`
    );

    return `https://wa.me/${STUDIO_WHATSAPP}?text=${text}`;
  };

  const buildWhatsappProjectLink = () => {
    const selectedNames = characterSlots
      .map((slot, index) => `${index + 1}. ${slot.selected?.name || slot.query || '-'}`)
      .join('\n');

    const eliminatedNames = eliminatedSlots
      .map((slotIndex) => characterSlots[slotIndex]?.selected?.name || characterSlots[slotIndex]?.query || '-')
      .join(' / ');

    const text = encodeURIComponent(
      `Olá! Quero garantir minha tattoo da Death Note Wishlist.\n\nNomes escritos no caderno:\n${selectedNames}\n\nResultado do D3: ${d20Result || '-'}\nNomes riscados: ${eliminatedNames || '-'}\nNome que sobrou: ${winnerCharacter?.name || '-'}\nOrigem: ${winnerCharacter?.subtitle || '-'}\nDesconto recebido: ${DISCOUNT_PERCENT}% OFF\nValor promocional: ${currencyBRL.format(FINAL_PRICE)} à vista no Pix\n\nQuero falar sobre esse projeto.`
    );

    return `https://wa.me/${STUDIO_WHATSAPP}?text=${text}`;
  };

  const resetDeathNoteWishlist = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCharacterSlots([initialCharacterSlot(), initialCharacterSlot(), initialCharacterSlot()]);
    setWinnerCharacter(null);
    setWinnerCharacterImage('');
    setEliminatedSlots([]);
    setD20Value(3);
    setD20Rolling(false);
    setD20Result(null);
    setSelectionLocked(false);
    setRaffleLoading(false);
    setRaffleMessage('');
  };

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const loadProjectImages = async () => {
      setApiGeekProjects((currentProjects) =>
        currentProjects.map((project) => ({
          ...project,
          apiStatus: 'loading',
        }))
      );

      const projectsWithApiImages = await Promise.all(
        GEEK_PROJECTS.map((project) =>
          fetchProjectImageFromOnePieceApi(project, controller.signal).catch((error) => {
            if (error?.name === 'AbortError') return null;

            return {
              ...project,
              image: '',
              apiName: project.wikiTitle,
              apiStatus: 'error',
              apiSource: '',
            };
          })
        )
      );

      if (!isMounted) return;

      setApiGeekProjects(
        projectsWithApiImages.map((project, index) => {
          const fallbackProject = GEEK_PROJECTS[index];

          return (
            project || {
              ...fallbackProject,
              image: '',
              apiName: fallbackProject.wikiTitle,
              apiStatus: 'error',
              apiSource: '',
            }
          );
        })
      );
    };

    loadProjectImages();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const styleId = 'ink-login-screen-styles';
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      styleTag.innerHTML = injectedStyles;
      document.head.appendChild(styleTag);
    }

    return () => {
      Object.values(searchTimersRef.current).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  return (
    <div className="ink-login-screen">
      {!WISHLIST_CLOSED && DEATH_NOTE_PREVIEW_ACTIVE && (
        <audio
          ref={bgAudioRef}
          src={SITE_MUSIC_SRC}
          preload="auto"
          autoPlay
          loop
          playsInline
        />
      )}

   

      {!WISHLIST_CLOSED && DEATH_NOTE_PREVIEW_ACTIVE && (
        <svg className="ink-blood-svg-defs" aria-hidden="true" focusable="false">
          <filter id="inkBloodRoughFilter" x="-20%" y="-220%" width="140%" height="520%" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.018 0.62" numOctaves="3" seed="17" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="7" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </svg>
      )}

      <div className="ink-login-shell">
        <header className="ink-header">
          <div className="ink-enter-top">
            <button
              type="button"
              className="ink-btn ink-btn-outline"
              onClick={() => setAuthModalOpen(true)}
            >
              Entrar
            </button>
          </div>

          <div className="ink-brand-wrap">
            <h1 className="ink-brand">
              <span className="ink-brand-main">INK</span>
              <span className="ink-brand-sub">RODRIGUEZ</span>
            </h1>
          </div>
        </header>

        <section className={`ink-main-card ${publicView === 'inicio' && !WISHLIST_CLOSED && DEATH_NOTE_PREVIEW_ACTIVE ? 'ink-main-card-death' : ''}`}>
          <div className="ink-main-card-inner">
            <div className="ink-public-switch">
              <button
                type="button"
                className={`ink-public-switch-btn ${publicView === 'inicio' ? 'active' : ''}`}
                onClick={() => setPublicView('inicio')}
              >
                Projetos Pessoais
              </button>

              <button
                type="button"
                className={`ink-public-switch-btn ${publicView === 'curso' ? 'active' : ''}`}
                onClick={() => setPublicView('curso')}
              >
                Curso de Tatuagem
              </button>
            </div>

          {publicView === 'inicio' ? (
            <div className="ink-public-block">
              <div className="ink-projects-wrap">
                <div className="ink-projects-hero">
                  <h2>PROJETOS PESSOAIS:</h2>
                  <p>
                    Separei alguns projetos que gostaria de tatuar com base na minha nova proposta de trabalho.
                    <br />
                    Todos esses projetos estarão com 50% de desconto.
                    <br />
                    Esses projetos podem ser feitos em qualquer área do corpo, porém, minha principal intenção seria um fechamento de ombro à mão, podendo conter símbolos e parte do personagem agregados.
                  </p>
                </div>

                <section className="ink-project-section">
                  <div className="ink-project-section-head">
                    <span className="ink-project-section-kicker">Projetos Geek</span>
                    <h3>One Piece</h3>
                    <p>
                      Escolha o personagem e chame no WhatsApp para garantir o projeto pessoal.
                    </p>
                  </div>

                  <div className="ink-project-grid">
                    {apiGeekProjects.map((project) => (
                      <article className="ink-project-card" key={project.name}>
                        <div className="ink-project-card-image">
                          {project.image ? (
                            <img
                              src={project.image}
                              alt={project.name}
                              loading="lazy"
                              onError={(event) => {
                                event.currentTarget.style.display = 'none';
                                event.currentTarget.parentElement?.classList.add('is-image-error');
                              }}
                              style={{ '--project-image-position': project.imagePosition || 'top center' }}
                            />
                          ) : null}

                          <div className={`ink-project-card-placeholder ${project.image ? 'ink-project-card-placeholder-hidden' : ''}`}>
                            <strong>{project.initials}</strong>
                          </div>
                        </div>

                        <div className="ink-project-card-body">
                          <h4>{project.name}</h4>
                          <span>{project.universe}</span>

                          <small className="ink-project-card-api-status">
                            {project.apiStatus === 'loading'
                              ? 'Carregando imagem...'
                              : project.image
                                ? `Imagem via ${project.apiSource || 'API'}`
                                : 'Imagem não encontrada'}
                          </small>

                          <a
                            className="ink-project-whatsapp-btn"
                            href={buildPersonalProjectWhatsappLink(project)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Chamar no WhatsApp
                          </a>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>

          
              </div>
            </div>
          ) : (
            <div className="ink-public-block">
              <div className="ink-course-wrap">
                <div className="ink-course-hero">
                  <span className="ink-course-kicker">🎓 Curso Profissionalizante</span>
                  <h2>Mais do que aprender a tatuar.</h2>
                  <p>
                    Uma formação completa pra você dominar técnica, projeto e mercado — com acompanhamento real.
                  </p>
                </div>

                <div className="ink-course-metrics">
                  <div className="ink-course-metric">
                    <strong>26</strong>
                    <span>Encontros presenciais</span>
                  </div>

                  <div className="ink-course-metric">
                    <strong>+30h</strong>
                    <span>Vídeo aulas</span>
                  </div>

                  <div className="ink-course-metric">
                    <strong>2</strong>
                    <span>Tatuagens supervisionadas</span>
                  </div>

                  <div className="ink-course-metric">
                    <strong>116h</strong>
                    <span>Carga horária total</span>
                  </div>
                </div>

                <section className="ink-course-section">
                  <h3>📚 Estrutura (Atualizada)</h3>
                  <ul className="ink-course-list">
                    <li>26 encontros presenciais (teoria + prática progressiva)</li>
                    <li>30h de vídeo aulas complementares</li>
                    <li>Estágio supervisionado com 2 tatuagens reais (mínimo 8h)</li>
                    <li>1 dia de Shadow (acompanhar uma tatuagem feita por mim)</li>
                    <li>Kit de Tatuagem Incluso</li>
                    <li>Carga horária total do programa: 116 horas</li>
                  </ul>
                </section>

                <section className="ink-course-section">
                  <h3>🗓️ Programa Presencial (26 Encontros)</h3>
                  <p>
                    Aulas de 3h cada. Sequência pensada pra evoluir sem pular etapas.
                  </p>

                  <div className="ink-course-grid" style={{ marginTop: 14 }}>
                    <div className="ink-course-group">
                      <h4>🖊️ Base Artística</h4>
                      <ul className="ink-course-list">
                        <li>Dia 1: Desenho (2D + 3D)</li>
                        <li>Dia 2: Perspectiva + encaixe de objetos</li>
                      </ul>
                    </div>

                    <div className="ink-course-group">
                      <h4>🧼 Fundamentos Profissionais</h4>
                      <ul className="ink-course-list">
                        <li>Dia 3: Biossegurança + pele</li>
                        <li>Dia 4: Máquinas e regulagem</li>
                        <li>Dia 5: Agulhas</li>
                      </ul>
                    </div>

                    <div className="ink-course-group">
                      <h4>🔥 Prática na Pele Artificial</h4>
                      <ul className="ink-course-list">
                        <li>Dia 6: Traços</li>
                        <li>Dia 7: Sombra Round Liner</li>
                        <li>Dia 8: Sombra Magnum</li>
                        <li>Dia 9: Preto sólido</li>
                        <li>Dia 10: Cor</li>
                        <li>Dia 11: Texturas</li>
                      </ul>
                    </div>

                    <div className="ink-course-group">
                      <h4>🎨 Estilos (1 dia cada)</h4>
                      <ul className="ink-course-list">
                        <li>Dia 12: Escritas / Fineline</li>
                        <li>Dia 13: Old School</li>
                        <li>Dia 14: New School</li>
                        <li>Dia 15: Blackwork</li>
                        <li>Dia 16: Realismo</li>
                        <li>Dia 17: Geek</li>
                        <li>Dia 18: Aquarela</li>
                        <li>Dia 19: Neo Tradicional</li>
                        <li>Dia 20: Pontilhismo</li>
                      </ul>
                    </div>

                    <div className="ink-course-group">
                      <h4>🧴 Cliente / Aftercare</h4>
                      <ul className="ink-course-list">
                        <li>Dia 21: Pré, durante e pós tattoo</li>
                      </ul>
                    </div>

                    <div className="ink-course-group">
                      <h4>👀 Shadow (Aula Viva)</h4>
                      <ul className="ink-course-list">
                        <li>Dia 22: Acompanhar uma tatuagem feita por mim</li>
                      </ul>
                    </div>

                    <div className="ink-course-group">
                      <h4>🧠 Projeto + Prova Prática (novo modelo)</h4>
                      <ul className="ink-course-list">
                        <li>Dia 23: Desenvolvimento de projeto</li>
                        <li>Dia 24: Dia do seu projeto (Prova prática completa)</li>
                      </ul>
                      <p>
                        Simulação real: o estúdio te manda referências como “cliente”, você cria o projeto,
                        apresenta, prepara o processo e executa seguindo padrão profissional
                        (inclui confirmação de presença e pós-tattoo).
                      </p>
                    </div>

                    <div className="ink-course-group">
                      <h4>💰 Precificação / Financeiro</h4>
                      <ul className="ink-course-list">
                        <li>Dia 25: Pix, cartão, taxas, conta, maquininha, controle e margem</li>
                      </ul>
                    </div>

                    <div className="ink-course-group">
                      <h4>🏁 Mercado e Profissionalização</h4>
                      <ul className="ink-course-list">
                        <li>Dia 26: Captação, Instagram, postura, estúdio, residência e evolução</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section className="ink-course-section">
                  <h3>🩸 Estágio Supervisionado (Obrigatório)</h3>
                  <ul className="ink-course-list">
                    <li>2 tatuagens reais supervisionadas</li>
                    <li>Mínimo de 8 horas de prática em pele humana</li>
                    <li>Deve conter: 1 linework + 1 sombra ou cor</li>
                    <li>O certificado é entregue após a conclusão do estágio.</li>
                  </ul>
                </section>

                <section className="ink-course-section">
                  <h3>🏆 Benefícios Inclusos</h3>
                  <ul className="ink-course-list">
                    <li>Certificado A3 físico</li>
                    <li>Apostila completa</li>
                    <li>Material incluso até a conclusão</li>
                    <li>30h de vídeo aulas</li>
                    <li>Acompanhamento pós-curso</li>
                    <li>Possibilidade de residência no Estúdio Shodō</li>
                    <li>Kit incluso</li>
                  </ul>
                </section>

                <section className="ink-course-section">
                  <h3>💰 Investimento</h3>

                  <div className="ink-course-pricing">
                    <div className="ink-course-price-card">
                      <span>Pix/Dinheiro</span>
                      <strong>R$ 3.190,00</strong>
                    </div>

                    <div className="ink-course-price-card">
                      <span>Cartão</span>
                      <strong>R$ 3.690,00</strong>
                    </div>

                    <div className="ink-course-price-card">
                      <span>Parcelamento</span>
                      <strong>em até 12x sem juros</strong>
                    </div>
                  </div>

                  <div className="ink-course-cta-row">
                    <a
                      href="https://wa.me/5524993087471"
                      target="_blank"
                      rel="noreferrer"
                      className="ink-btn ink-btn-green"
                    >
                      Reservar agora
                    </a>

                    <a
                      href="https://www.instagram.com/ink.rodriguez/"
                      target="_blank"
                      rel="noreferrer"
                      className="ink-btn ink-btn-outline"
                      style={{ width: 'auto', minWidth: 'unset', padding: '13px 18px' }}
                    >
                      Ver no Instagram
                    </a>
                  </div>

                  <p className="ink-course-note">
                    📍 Estúdio Shodō — Volta Redonda/RJ • Vagas limitadas
                  </p>
                </section>
              </div>
            </div>
          )}
          </div>
        </section>

        {authModalOpen && (
          <div className="ink-modal-overlay" onClick={() => setAuthModalOpen(false)}>
            <div className="ink-modal" onClick={(e) => e.stopPropagation()}>
              <button className="ink-close" onClick={() => setAuthModalOpen(false)}>
                ✕
              </button>

              <h2>Entrar</h2>
              <p className="ink-auth-subtitle">Acesse sua conta para continuar.</p>

              <form className="ink-form" onSubmit={handleLogin}>
                <div className="ink-form-group">
                  <label htmlFor="ink-login-email">E-mail</label>
                  <input
                    id="ink-login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seuemail@exemplo.com"
                    autoComplete="email"
                  />
                </div>

                <div className="ink-form-group">
                  <label htmlFor="ink-login-password">Senha</label>
                  <input
                    id="ink-login-password"
                    type="password"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    placeholder="Sua senha"
                    autoComplete="current-password"
                  />
                </div>

                <button
                  type="submit"
                  className="ink-btn ink-btn-primary ink-full"
                  disabled={loadingAuth}
                >
                  {loadingAuth ? 'Entrando...' : 'Entrar'}
                </button>

                {authMessage ? <p className="ink-status-text">{authMessage}</p> : null}
              </form>
            </div>
          </div>
        )}

              </div>
    </div>
  );
}